import {

  BoxGeometry,
  MeshNormalMaterial,
  Mesh,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
  HemisphereLight,
  PointLight,
  AmbientLight,
  Vector2,
  Raycaster,
  MeshBasicMaterial,
  MeshPhongMaterial,
  Color,
  Vector3,
  MathUtils,
  DirectionalLight,
  Box3,
  GridHelper,
  Matrix4,
  MOUSE,
  OrthographicCamera,
  Quaternion,
  Sphere,
  Spherical,
  Vector4,
  Clock,
  DoubleSide,
  BoundingBoxHelper,
  BufferGeometry,
  Object3D,
  Fog,
  SphereGeometry,
  BackSide,
  ShaderMaterial,
  InstancedBufferAttribute,
  ColorManagement,
  LinearSRGBColorSpace,
  LineSegments,
  LineBasicMaterial,
  Float32BufferAttribute
} from 'three'

import { selectGridSource, buildGridDraw, gridConfidence } from './buildingGrid'
import { selectionForHide, pageSelectionOf } from './hideSelection'
import { expandToFamily, hasParts, resolveToElement } from '../loader/streaming/nestedParts'
import GridLabelOverlay from './gridLabelOverlay'

import Stats from 'three/examples/jsm/libs/stats.module.js'
import { ViewHelper } from '../view-helper';

import {
  SetChildrenMaterial,
  GetChildrenMeshes,
  SetChildrenMaterialsBack,
  MergeChildren,
  UpdateGroupVisibility
} from '../../../helpers/three'

import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import CameraControls from 'camera-controls'

import TWEEN from '@tweenjs/tween.js'
import { PostProcessing } from './postProcessing/postProcessing'
import { Dimensions } from './features/measurement/dimensions'
import { Clipper } from './features/clipping/clipper'
import { intersectionIndicator } from './features/crosshair'
import { Box3Helper } from 'three'
import { Material } from 'three'
import RenderOnDemand, { renderOnDemandEnabled } from './renderOnDemand'
import { sceneVersion } from '../loader/streaming/renderSignal'
import { setSurfaceShading, surfaceShadingState } from '../loader/streaming/surfaceShading';
import { planFraming, uidsInScreenRect } from './frameSelection';
import { makePublisher, inProgressState, finalState, diffSets } from './liveSelection';
import { cssPixelScale, overlayRectStyle } from './overlayGeometry';

import { shouldIgnoreKey } from './keyGuard';
import { saveDataUrl } from './saveDataUrl';
import SurfaceComposite, { compositeState } from './composite/surfaceComposite';
import { url as API_URL, tokenRef as API_TOKEN_REF } from 'api';
// REPORT THE VIEWER'S HALF TO THE SERVER TOO (owner, 2026-10-21: the trace
// must come "from threejs 3d and from reactjs tree"). The page posts its
// chain to /client-log; without this the 3D half existed only in the browser
// console, which is exactly the copying the server-side trace exists to
// avoid. Best effort, throttled, never throws into a click.
let _traceSent = 0; let _traceWindow = 0;
function reportViewerTrace(where, data) {
  try {
    const now = Date.now();
    if (now - _traceWindow > 60000) { _traceWindow = now; _traceSent = 0; }
    if (_traceSent >= 10) return;
    _traceSent += 1;
    // The app's own base and token key - api/index.js derives the key from
    // the host, so predeployment and prod do not share one. Guessing a name
    // here would post nowhere and look like a chain that stopped.
    const base = API_URL;
    const body = {
      where: String(where).slice(0, 60),
      page: typeof window !== 'undefined' ? window.location.href : '',
      message: JSON.stringify(data).slice(0, 380),
      stack: '',
    };
    const headers = { 'Content-Type': 'application/json' };
    try {
      const t = typeof localStorage !== 'undefined' && localStorage.getItem(API_TOKEN_REF);
      if (t) headers.Authorization = `Bearer ${t}`;
    } catch (e) { /* no token */ }
    fetch(`${base}/client-log`, { method: 'POST', headers, body: JSON.stringify(body), keepalive: true }).catch(() => {});
  } catch (e) { /* a trace must never be the error */ }
}
// The colour a box-drag paints WHILE it is being dragged. Deliberately
// not the selection colour: what is under the rectangle is not selected
// until the button is released.
const BOX_PREVIEW_COLOR = 0x2F6FED;

// A uid can live in a SLOT of a shared mesh - an InstancedMesh pool, or
// the BatchedMesh a tile uses for the fragments that appear once or
// twice in it - or it can be a Mesh of its own. Selection, deselection
// and isolate care about that distinction and not about which of the two
// shared kinds it is, so they ask this rather than for isInstancedMesh:
// a batched item asked the old question answered "no" and fell through
// to the whole-mesh path, which tinted or revealed an entire batch.
const isSlotBacked = (mesh) => !!(mesh
  && (mesh.isInstancedMesh || mesh.isBatchedMesh || mesh.isSlotMesh));

// Which element a raycast hit, whatever kind of mesh reported it.
//
// A pooled instance reports instanceId. An item in a tile batch reports
// batchId. A tile's merged singleton mesh reports neither - it is one
// plain Mesh - so its element index comes from the per-vertex index the
// exporter baked in, read off any vertex of the hit face.
function pickedIndex(hit) {
  if (!hit) return undefined;
  if (hit.instanceId !== undefined) return hit.instanceId;
  if (hit.batchId !== undefined) return hit.batchId;
  const object = hit.object;
  if (object && typeof object.elementIndexAt === 'function' && hit.face) {
    const index = object.elementIndexAt(hit.face.a);
    if (index >= 0) return index;
  }
  return undefined;
}

// A silhouette around ONE element.
//
// The outline pass takes whole OBJECTS, and a pooled element's object is
// the InstancedMesh holding every instance of its geometry - so asking it
// to outline a hovered pipe outlined every pipe of that type in the
// building. The owner's words: "it highlights all instances of the same
// fragment".
//
// So the element is pulled out: a throwaway Mesh sharing the pool's
// geometry, placed at that one instance's transform. It writes neither
// colour nor depth, so it is invisible in the beauty pass and cannot
// z-fight the instance it sits on; the outline pass substitutes its own
// materials and needs only the geometry and the matrix. One object, one
// silhouette.
//
// All three kinds of slot-backed mesh, because packing must not cost the
// user the highlight. Where the geometry comes from differs:
//
//   pool    the InstancedMesh's own geometry, placed by the slot matrix
//   batch   the shape the item was added with, kept on its row, placed
//           the same way - BatchedMesh will not hand it back
//   merge   one plain Mesh with every element's vertices already in
//           place, so the element is a SLICE of the shared index buffer:
//           a geometry sharing the same attributes with its own draw
//           range, and the mesh's own world matrix.
//
// A merged element needs the exporter's size ordering for that slice; an
// older merge without it still gets no silhouette, which is the one
// remaining gap and is visible as "no highlight", never as the wrong one.
function pullOutGeometry(object, index) {
  if (object.isInstancedMesh) return object.geometry;
  if (typeof object.elementRangeAt === 'function') {
    const range = object.elementRangeAt(index);
    if (!range || !object.geometry) return null;
    // Shares the attribute and index buffers - this allocates a
    // descriptor, not a copy of the mesh.
    const slice = new BufferGeometry();
    for (const name of Object.keys(object.geometry.attributes)) {
      slice.setAttribute(name, object.geometry.attributes[name]);
    }
    if (object.geometry.index) slice.setIndex(object.geometry.index);
    slice.setDrawRange(range.start, range.count);
    return slice;
  }
  const row = object.userData && object.userData[index];
  return (row && row.__pullGeometry) || null;
}

function buildPullOut(hit) {
  const object = hit && hit.object;
  const index = pickedIndex(hit);
  if (!object || index === undefined || !isSlotBacked(object)) return null;
  // Hidden by the state store or below the size floor. A merged mesh
  // keeps that in a texture rather than in its matrix, so it has to be
  // asked; a raycast still hits geometry the shader is discarding.
  if (typeof object.isItemVisible === 'function' && !object.isItemVisible(index)) return null;

  const geometry = pullOutGeometry(object, index);
  if (!geometry) return null;

  const mesh = new Mesh(geometry, new MeshBasicMaterial({
    colorWrite: false, depthWrite: false, depthTest: true,
  }));
  mesh.name = 'outline:pullout';
  mesh.frustumCulled = false;
  mesh.matrixAutoUpdate = false;

  // A merged element carries its own placement in its vertices, so the
  // mesh's world matrix IS the transform. Everything else is a slot.
  if (typeof object.elementRangeAt === 'function') {
    mesh.matrixWorld.copy(object.matrixWorld);
    return mesh;
  }
  if (typeof object.getMatrixAt !== 'function') return null;
  const matrix = new Matrix4();
  object.getMatrixAt(index, matrix);
  // A zero-scaled slot is a hidden or duplicate instance; outlining it
  // would draw a silhouette around nothing.
  const scale = new Vector3();
  matrix.decompose(new Vector3(), new Quaternion(), scale);
  if (scale.x < 0.001) return null;
  mesh.matrix.copy(matrix);
  mesh.matrixWorld.multiplyMatrices(object.matrixWorld, matrix);
  return mesh;
}

export function pickedUid(hit) {
  const index = pickedIndex(hit);
  if (index === undefined) return null;
  const data = hit.object && hit.object.userData;
  const entry = data && data[index];
  return entry && entry.uniqueId ? entry.uniqueId : null;
}


const subsetOfTHREE = {
  MOUSE,
  Vector2,
  Vector3,
  Vector4,
  Quaternion,
  Matrix4,
  Spherical,
  Box3,
  Sphere,
  Raycaster,
  MathUtils: {
    DEG2RAD: MathUtils.DEG2RAD,
    clamp: MathUtils.clamp
  }
}

const measureCursor =
  'url(data:image/png;base64,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), auto'

let stats
let updateFcts = []
var lastTimeMsec = null

// Selection-highlight material for the legacy non-instanced (regular
// Mesh) path. The streaming InstancedMesh path doesn't use this — its
// highlight is a per-instance color write into instanceColor, applied
// against the slot's base material, so this material is only seen on
// projects rendered through the legacy GLB Loader.
//
// DoubleSide so the highlight renders on inward-facing faces too —
// matters when the camera ends up inside a selected element (a wall
// volume, a duct interior, etc.) and the selection would otherwise
// disappear because back-face culling drops the only visible faces.
//
// Transparency settings unchanged: alphaTest 0.5 + opacity 0.6 gives
// a translucent overlay that still resolves at the depth of the
// underlying geometry; this is also what the gray "disabled" tree
// highlight uses, since every tree's selector flows through the
// shared getMaterialForColor cache below.
const createSelectionMaterial = (color) => new MeshBasicMaterial({
  color,
  wireframe: false,
  transparent: true,
  alphaTest: 0.5,
  opacity: 0.6,
  side: DoubleSide,
});

const materialCache = new Map();

const getMaterialForColor = (color) => {
  const key = color.toString();
  if (!materialCache.has(key)) {
    materialCache.set(key, createSelectionMaterial(color));
  }
  return materialCache.get(key);
};

// A raycast hit on an instanced / batched item whose matrix is collapsed
// (hidden or not shown yet) - not something the user can see.
const _hitMatrix = new Matrix4()
function collapsedItemHit(h) {
  const o = h && h.object
  const id = h && (h.instanceId !== undefined ? h.instanceId : h.batchId)
  if (!o || id === undefined || typeof o.getMatrixAt !== 'function') return false
  try { o.getMatrixAt(id, _hitMatrix) } catch (e) { return false }
  return Math.abs(_hitMatrix.determinant()) < 1e-9
}

export default class Viewer {
  
  // Build a Set of selected uniqueIds
  _buildSelectedIdSet() {
    const set = new Set();
    if (Array.isArray(this.totalSelection)) {
      for (const id of this.totalSelection) set.add(id);
    }
    return set;
  }

  // Get selected instance indices for an InstancedMesh
  _getSelectedInstanceIndices(instancedMesh, selectedIdsSet) {
    if (!selectedIdsSet || selectedIdsSet.size === 0) return null;
    const indices = [];
    const ud = instancedMesh.userData || {};
    // userData is an Array (slot index -> entry) plus named side-properties
    // like snapshotMatrices/baseColors/currentLod/hash. Iterate the
    // numeric range explicitly so parseInt("snapshotMatrices") = NaN
    // can never sneak into the indices array.
    // A merged tile mesh's userData is a plain object (rows by index): its
    // item count comes from the mesh.
    const len = (typeof ud.length === 'number') ? ud.length : (instancedMesh.batchItemCount || 0);
    for (let i = 0; i < len; i++) {
      const val = ud[i];
      if (!val || typeof val.uniqueId === 'undefined') continue;
      if (selectedIdsSet.has(val.uniqueId)) indices.push(i);
    }
    return indices.length ? indices : null;
  }

  // Expand box by object's world-space bounds
  _expandWorldBoxByObject(obj, outBox, onlyInstanceIndices = null) {
    if (!obj) return outBox;

    // A tile's BatchedMesh or merged mesh (tileBatch / tileMerge): per ITEM,
    // never the whole object - setFromObject framed the entire tile, and a
    // batch's own bounds also take in the items parked at the origin while
    // hidden or not yet shown (owner, 2026-09-19: framing wrong on some
    // fragments).
    if (obj.isBatchedMesh || (obj.isSlotMesh && obj.userData && obj.userData.itemCentres)) {
      return this._expandBoxBySlotItems(obj, outBox, onlyInstanceIndices);
    }

    if (!obj.isInstancedMesh) {
      obj.updateWorldMatrix(true, true);
      this._boxTmp.setFromObject(obj);
      if (!this._boxTmp.isEmpty()) outBox.union(this._boxTmp);
      return outBox;
    }

    const inst = obj;
    const geom = inst.geometry;
    if (!geom) return outBox;

    if (!geom.boundingBox) geom.computeBoundingBox();
    if (!geom.boundingBox) return outBox;

    const count = inst.count ?? 0;
    let indices = onlyInstanceIndices;
    if (!indices) {
      indices = [...Array(count).keys()];
    }

    inst.updateWorldMatrix(true, false);

    for (let i = 0; i < indices.length; i++) {
      const idx = indices[i];
      if (idx < 0 || idx >= count) continue;

      // For visibility framing we want the IDEAL position of each
      // instance, not the current matrix — which might be zero-scaled
      // due to hide/isolate. Read from snapshotMatrices when available
      // (streaming path); fall back to instanceMatrix for legacy
      // non-streaming meshes that don't have a snapshot.
      const snapshot = inst.userData?.snapshotMatrices;
      if (snapshot) {
        this._matrixTmp.fromArray(snapshot, idx * 16);
      } else {
        inst.getMatrixAt(idx, this._matrixTmp);
      }

      this._worldTmp.multiplyMatrices(inst.matrixWorld, this._matrixTmp);

      this._geomBoxTmp.copy(geom.boundingBox).applyMatrix4(this._worldTmp);
      outBox.union(this._geomBoxTmp);
    }

    return outBox;
  }

  // Items of a tile batch / merged mesh into a world box, from where each
  // item sits when visible (snapshotMatrices) - not its current matrix,
  // which is a zero-scale sentinel at the origin while it is hidden.
  _expandBoxBySlotItems(obj, outBox, indices = null) {
    const ud = obj.userData || {};
    const count = obj.isBatchedMesh ? (obj.batchItemCount || (ud.snapshotMatrices ? ud.snapshotMatrices.length / 16 : 0)) : (ud.itemRadii ? ud.itemRadii.length : 0);
    const list = indices || [...Array(count).keys()];
    obj.updateWorldMatrix(true, false);
    for (const idx of list) {
      if (idx < 0 || idx >= count) continue;
      if (obj.isBatchedMesh) {
        if (typeof obj.getBoundingBoxAt !== 'function') continue;
        let local = null;
        try { local = obj.getBoundingBoxAt(idx, this._geomBoxTmp); } catch (e) { local = null; }
        if (!local || local.isEmpty()) continue;
        if (ud.snapshotMatrices) this._matrixTmp.fromArray(ud.snapshotMatrices, idx * 16);
        else obj.getMatrixAt(idx, this._matrixTmp);
        this._worldTmp.multiplyMatrices(obj.matrixWorld, this._matrixTmp);
        outBox.union(local.applyMatrix4(this._worldTmp));
      } else {
        const r = ud.itemRadii[idx];
        if (!(r > 0)) continue;
        const c = ud.itemCentres; const o = idx * 3;
        this._geomBoxTmp.min.set(c[o] - r, c[o + 1] - r, c[o + 2] - r);
        this._geomBoxTmp.max.set(c[o] + r, c[o + 1] + r, c[o + 2] + r);
        outBox.union(this._geomBoxTmp.applyMatrix4(obj.matrixWorld));
      }
    }
    return outBox;
  }

  // Find the bbox for a uid that's not currently rendered (tile unloaded
  // OR isolated/hidden). Walks the streaming registry's loaded sidecars
  // looking for the uid; returns the host tile's bbox. Used as a frame
  // fallback so "click selected → frame" still points the camera in the
  // right direction even when geometry isn't materialized.
  _findTileBoxForUid(uid) {
    // Fast path: when streaming is active, the adapter maintains a
    // uid -> [{tileKey, slotIndex}] reverse index built by
    // MapMeshesAdapter. We only need the tile box, so just resolve the
    // first entry's tileKey via the streaming registry. This avoids
    // walking every tile's instancesByHash per uid.
    const sl = (typeof window !== 'undefined') ? window.__sl : null;
    if (this.streaming?.state) {
      const adapter = sl?.streamingController?.adapter;
      const registryFast = sl?.streamingController?.registry;
      const entries = adapter?.getActiveSlotsForUid
        ? adapter.getActiveSlotsForUid(uid)
        : null;
      if (entries && entries.length > 0 && registryFast) {
        const wantedKey = entries[0].tileKey;
        let foundBox = null;
        registryFast.forEach(tile => {
          if (foundBox) return;
          if (tile.key === wantedKey) foundBox = tile.box;
        });
        if (foundBox) return foundBox;
      }
      // Fallthrough: uid not in adapter (e.g. tile unloaded). Use the
      // slow walk so isolated/unloaded uids still resolve.
    }

    const registry = sl?.streamingController?.registry;
    if (!registry) return null;

    let foundBox = null;
    registry.forEach(tile => {
      if (foundBox) return;
      if (!tile.instancesByHash) return;
      for (const [, instances] of tile.instancesByHash) {
        for (const inst of instances) {
          if (inst.uniqueId === uid) {
            foundBox = tile.box;
            return;
          }
        }
      }
    });
    return foundBox;
  }

  // Progress mode & auto-spin API
  autoSpin = false;
  progressMode = false;
  progressShown = new Set();

  enableProgressMode = ({ startHidden = true } = {}) => {
    this.progressMode = true;
    if (!this.progressShown) this.progressShown = new Set();
    if (startHidden) this.hideAllGeometry();
  };

  hideAllGeometry = () => {
    for (const [, group] of this.meshesObject.entries()) {
      if (group?.mesh) group.mesh.visible = false;
      UpdateGroupVisibility(group, false);
    }
    this.totalSelection = [];
  };

  setVisibleByProgress = (uniqueIds = [], { accumulate = true, frame = false } = {}) => {
    if (!accumulate) {
      this.hideAllGeometry();
      this.progressShown.clear();
    }

    const input = Array.isArray(uniqueIds) ? uniqueIds : [uniqueIds];
    const ids = input.filter(id => this.meshesObject.has(id));

    ids.forEach((id) => {
      const grp = this.meshesObject.get(id);
      if (!grp) return;
      UpdateGroupVisibility(grp, true);
      if (grp.mesh) grp.mesh.visible = true;
      this.progressShown.add(id);
    });

    const fromSet = (this.progressShown && this.progressShown.size)
      ? Array.from(this.progressShown)
      : ids;

    this.totalSelection = fromSet;

    if (frame && this.totalSelection.length) {
      requestAnimationFrame(() => this.targetItems());
    }
  };

  startAutoSpin = () => { this.autoSpin = true; };
  stopAutoSpin = () => { this.autoSpin = false; };
  toggleAutoSpin = () => { this.autoSpin = !this.autoSpin; };

  constructor(container, ReactAPI, line, socketviewer, isIfcProject) {
    this.isIfcProject = isIfcProject
    this.loadedModels = []
    this.clock = new Clock()
    this.line = line
    this.socketviewer = socketviewer
    this.container = container
    this.ReactApi = ReactAPI
    this.checkboxTooltip = document.getElementById('checkboxTooltip')
    this.divSize = {}
    this.intersects = []
    this.prevMeshes = [{ parentNodeId: '', meshes: [] }]
    this.prevMaterials = [{ parentNodeId: '', materials: [] }]

    this.prevGroupMeshes = []
    this.meshes = []
    this.clippingHelpers = []
    this.prevGroupMaterials = []
    this.prevUnmatchedMeshes = []
    this.prevUnmatchedMaterials = []
    this.prevExistingMeshes = []
    this.prevExisitingMaterials = []
    this.prevDemolitionMeshes = []
    this.prevDemolitionMaterials = []
    this.prevFilesMeshes = []
    this.prevFilesMaterials = []
    
    this.selectedGroup = {}
    this.mouse = new Vector2()
    this.raycaster = new Raycaster()
    this.meshesObject = new Map()
    this.coordsMouse = { x: null, y: null }

    this.intersectionIndicator = intersectionIndicator

    this.totalSelection = []
    this.totaljsonIds = []
    this.isolateMode = false
    
    this.vertexShader = null
    this.fragmentShader = null
    
    this.uniqueMeshes = new Map()
    this.cachedSelection = new Map()
    this.cachedMeshSelections = new Map()
    this.progressShown = new Set()

    // Temp objects for performance
    this._boxTmp = new Box3();
    this._geomBoxTmp = new Box3();
    this._matrixTmp = new Matrix4();
    this._worldTmp = new Matrix4();
    this._posTmp = new Vector3();
    this._quatTmp = new Quaternion();
    this._scaleTmp = new Vector3();

    this.init()

    // Kept so destroy() can remove it: a window listener outlives the page,
    // keeps the whole viewer reachable, and fired the shortcuts on every
    // viewer ever opened in the tab (S took one screenshot per dead one).
    this._onKeyDown = e => {
      if (this._destroyed) return
      // Never while someone is typing or filling in a dialog (keyGuard).
      if (shouldIgnoreKey(e)) return
      if (e.key == 'f' || e.code == 'KeyF') {
        // "F did nothing" must not be a silent outcome: a person cannot
        // tell a no-op from a bug, and this one HAS been a bug. ReactApi is
        // the viewer's existing channel to the page; `notify` is optional,
        // so a page that has not wired it is no worse off than today.
        const why = this.targetItems()
        // Written out rather than as ReactApi?.notify?.(...) - an optional
        // CALL as a statement trips no-unused-expressions, which jest never
        // sees and the build refuses.
        if (why === 'empty' && this.ReactApi && typeof this.ReactApi.notify === 'function') {
          this.ReactApi.notify('Nothing to frame yet - those elements have not loaded.')
        }
      } else if (e.key == 'h' || e.code == 'KeyH') {
        this.onInitialPosition()
      } else if (e.key == 'g' || e.code == 'KeyG') {
        this.onShowGrid(document.getElementById('gridToggleBtn'))
      } else if (e.key == 'i' || e.code == 'KeyI') {
        this.toggleIsolate(document.getElementById('isolateToggleBtn'))
      } else if (e.key == 's' || e.code == 'KeyS') {
        this.onScreenShot()
      } else if (e.key == 'm' || e.code == 'KeyM') {
        this.toggleMeasurement()
      } else if (e.key == 'c' || e.code == 'KeyC') {
        this.toggleClipping()
      }
    }
    window.addEventListener('keydown', this._onKeyDown, { bubbles: true, useCapture: true, passive: true })

    // BOX SELECT (owner, 2026-09-22: "ctrl + mouse click in space, no
    // fragment touched, creates a selection box, and every fragment in
    // there gets selected"). OPT-IN: only a page that hands the viewer a
    // boxSelect callback gets it; every other viewer page is unchanged.
    // It starts on a CAPTURE-phase pointerdown on the container, which runs
    // before camera-controls' own pointerdown on the canvas - so the orbit
    // is switched off before it can begin, not undone after it moved.
    this._installBoxSelect()

    this.renderer.domElement.addEventListener('mousedown', e => {
      // Client coordinates: the mouseup comparison below only needs "did
      // the pointer move between press and release", and clientX/Y is the
      // one measure that means the same thing in both handlers.
      this.coordsMouse.x = e.clientX
      this.coordsMouse.y = e.clientY
    }, { useCapture: true, passive: true })

    var points = [new Vector3(), new Vector3()]
    let clicks = 0
    let currentDimension = undefined
    
    this.renderer.domElement.addEventListener('mouseup', e => {
      if (e.clientX === this.coordsMouse.x && e.clientY === this.coordsMouse.y && e.button === 0) {
        this._pointerNdc(e)

        this.raycaster.setFromCamera(this.mouse, this.camera)
        this.intersects = this.getRaycasterIntersects()

        // A page asking for a point (the tracking page's two-point sheet
        // alignment) gets the click's world point, and nothing else acts
        // on that click.
        if (this.pickPointHandler && this.intersects[0]) {
          try { this.pickPointHandler(this.intersects[0].point.clone(), this.intersects[0]) } catch (err) { /* the page's handler */ }
          return
        }

        if (this.intersects[0]) {
          if (this.scene.measure) {
            this.dimensionClass.create(this.intersects)
          } else if (this.scene.clipping) {
            if (this.intersects[0].object.name === 'clipingHelper') {
              this.clipper.removeClippingPlane(this.intersects[0].object)
              const clipIndex = this.clippingHelpers.indexOf(this.intersects[0].object.parent)
              this.clippingHelpers.splice(clipIndex, 1)
              this.clipper.updateTooltip()
            } else {
              const clippingPlane = this.clipper.createPlane(this.intersects)
              // A face without a usable normal makes no plane.
              if (clippingPlane && clippingPlane.helper) this.clippingHelpers.push(clippingPlane.helper)
            }
          } else {
            // A pooled instance reports instanceId; an item inside a
            // tile batch reports batchId. Both index the same userData
            // shape, so the picker only has to know which field carried
            // the hit.
            const hit = this.intersects[0];
            const instanceId = pickedIndex(hit);
            const userData = hit.object.userData;

            // Why a click resolved nothing is invisible from outside:
            // the ray may have missed everything, or hit something the
            // picker cannot map to an element. Behind a flag so a
            // capture can see both cases without noise for users.
            if (typeof window !== 'undefined' && window.BIDLIGHT_PICK_DEBUG) {
              console.warn('[picker] hits', this.intersects.length, this.intersects.slice(0, 4).map(h => {
                return {
                  object: h.object && h.object.name,
                  type: h.object && h.object.type,
                  instanceId: h.instanceId,
                  batchId: h.batchId,
                  resolved: pickedUid(h),
                  distance: +h.distance.toFixed(2),
                };
              }));
            }

            if (userData && userData[instanceId] && userData[instanceId].uniqueId) {
              // TRACE (owner, 2026-10-21): say what the picker resolved before
              // handing it to the page, so a chain that stops can be read from
              // either end rather than inferred.
              const _pick = {
                uniqueId: userData[instanceId].uniqueId,
                instanceId,
                object: this.intersects[0] && this.intersects[0].object && this.intersects[0].object.name,
                ctrl: !!e.ctrlKey,
              };
              console.log('[trace picker] hit -> uid', _pick);
              reportViewerTrace('trace/3d-picker', _pick);
              this.ReactApi.findInJsonUsingViewer(userData[instanceId].uniqueId, e.ctrlKey);
            } else {
              // Diagnostic: visible geometry was raycast-hit but the picker
              // can't resolve a uniqueId. The most common causes are
              // (a) the hit was a non-InstancedMesh (instanceId undefined),
              // (b) the slot was released/regrown between materialize and
              // click. Logging lets us see which class is affected in F12.
              const obj = this.intersects[0].object;
              console.warn('[picker] click hit but unselectable',
                { instanceId,
                  objectType: obj?.type,
                  objectName: obj?.name,
                  hasUserDataAtIndex: !!userData?.[instanceId],
                  uniqueIdAtIndex: userData?.[instanceId]?.uniqueId,
                  poolHash: userData?.hash });
              // AN UNRESOLVABLE HIT IS NOTHING SELECTED, from the only point of
              // view that counts. The warning above says the picker could not
              // map it; the page still has to hear that the click resolved to
              // no element, or the selection silently stays as it was.
              this._reportPickMiss(e)
            }
          }
        } else {
          // A CLICK THAT HITS NOTHING CLEARS THE SELECTION (owner, 2026-09-30).
          //
          // This branch did not exist: 'if (this.intersects[0])' had no else, so
          // a click on empty space did nothing at all - no callback, nothing
          // cleared, and from outside that is indistinguishable from a broken
          // click. Ctrl-click on empty space was the report.
          //
          // SAFE FOR BOX SELECT, and that is why this is in mouseup rather than
          // click: the guard above only acts when the pointer did NOT move
          // between press and release, so a ctrl+DRAG - which is how a box
          // selection is made in empty space - never reaches here.
          //
          // Not while a tool owns the click: measure and clipping consume empty
          // clicks of their own, and pickPointHandler has already returned above.
          if (!this.scene.measure && !this.scene.clipping) this._reportPickMiss(e)
        }
      }
    }, { useCapture: true, passive: true })

    this.renderer.domElement.addEventListener('mousemove', e => {
      document.body.style.cursor = this.scene.measure ? measureCursor : 'auto'
      // Hover raycast at most every 80 ms: on a streamed model it walks
      // every pool mesh and its instances (1.3M on SpaceX), and mousemove
      // fires at pointer rate. Skipped while tiles are loading unless in
      // measure mode.
      const __now = (typeof performance !== 'undefined') ? performance.now() : Date.now()
      if (!this.scene.measure) {
        if (__now - (this._lastHoverRaycastMs || 0) < 80) return
        if (this._isStreamingBusy && this._isStreamingBusy()) return
      }
      this._lastHoverRaycastMs = __now

      this._pointerNdc(e)

      this.raycaster.setFromCamera(this.mouse, this.camera)
      this.intersects = this.getRaycasterIntersects()

      if (this.intersects[0]) {
        this._setHoverOutline(this.intersects[0])

        if (this.scene.measure) {
          const point = this.dimensionClass.getClosestVertex(this.intersects[0])
          this.intersectionIndicator.position.copy(point)

          const delta = this.clock.getDelta()
          if (this.dimensionClass && this.dimensionClass.dragging) {
            this.dimensionClass.update(delta, this.intersects)
          }
        }
      } else {
        // Pointer over nothing: the pull-out goes with the outline.
        this._setHoverOutline(null)
      }

      if (this.scene.clipping) {
        this.clipper.updateTooltip(this.intersects)
        this.clipper.updateTempClipper(this.intersects)
      }

      if (this.intersects[0] && (!this.line || this.line.rule.prices)) {
        if (this.checkboxTooltip != null) {
          if (this.checkboxTooltip.checked) {
            this.ReactApi.setIsPopOver(true)
            const local = this._pointerLocal(e)
            this.ReactApi.setCoordsForPopover({
              top: `${local.y}px`,
              left: `${local.x + 30}px`
            })
          }
        }

const hitObj = this.intersects[0].object;
const hitName = hitObj?.name ?? '';
const parentName = hitObj?.parent?.name ?? '';
// Legacy contract: name.length === 45 means it's a uniqueId-named mesh
// (the legacy non-streaming path). Otherwise the legacy code reads
// the parent's name. Under streaming, the global InstancedMesh's
// own name is "global_<hash-prefix>" (~30 chars) and parent is
// sceneRoot (no name). In both cases, fall back gracefully if either
// is missing rather than crashing.
// RESOLVE THE HOVER BY UID FIRST, AND FALL BACK TO THE NAME.
//
// The legacy contract is that a mesh NAME of exactly 45 characters is a
// uniqueId, and anything else means read the parent's name. Under streaming
// neither holds: the hit is the pool InstancedMesh named "global_<hash>" and
// its parent is sceneRoot, which has NO name - so this called
// onHoverMesh("") and SocketViewer did meshes.current.get("") -> undefined.
// The popover was handed nothing, silently, on every streamed model.
//
// The comment above described that mismatch and the line below it was left
// unchanged. Somebody knew.
//
// pickedUid(hit) reads the row's real uniqueId out of the instance userData,
// and meshes.current is keyed by UniqueId - so it is the value this handler
// already computes two lines up for the hover outline. The legacy name path
// is kept for the non-streaming viewer, which still names meshes by uid.
const hoveredUid = pickedUid(this.intersects[0]);
if (hoveredUid) {
  this.ReactApi.onHoverMesh(hoveredUid);
} else if (hitName.length !== 45) {
  this.ReactApi.onHoverMesh(parentName);
} else {
  this.ReactApi.onHoverMesh(hitName);
}

      } else if (!this.socketviewer) {
        this.ReactApi.setIsPopOver(false)
      }
    }, { useCapture: true, passive: true })

    this.renderer.domElement.addEventListener('mouseover', e => {
      this.renderer.mouseIsOver = true
    }, { useCapture: true, passive: true })

    this.renderer.domElement.addEventListener('mouseout', e => {
      this.renderer.mouseIsOver = false
      document.body.style.cursor = 'auto'
      // Drops the pull-out as well as the selection, or the silhouette
      // stays behind on a mesh nobody is pointing at.
      this._setHoverOutline(null)
    }, { useCapture: true, passive: true })
  }

  // Own the pull-out: one at a time, disposed when the hover moves.
  _setHoverOutline(hit) {
    const uid = pickedUid(hit)
    if (uid && uid === this._hoverOutlineUid) return
    this._hoverOutlineUid = uid || null

    if (this._hoverOutline) {
      if (this._hoverOutline.parent) this._hoverOutline.parent.remove(this._hoverOutline)
      // The geometry belongs to the pool and is NOT disposed here; the
      // throwaway material is ours.
      if (this._hoverOutline.material && this._hoverOutline.material.dispose) {
        this._hoverOutline.material.dispose()
      }
      this._hoverOutline = null
    }

    const pullOut = buildPullOut(hit)
    if (!pullOut) {
      this.postProcessing.outlinePass.selectedObjects = []
      return
    }
    this.scene.add(pullOut)
    this._hoverOutline = pullOut
    this.postProcessing.outlinePass.selectedObjects = [pullOut]
  }

  _installBoxSelect() {
    const canvas = this.renderer && this.renderer.domElement
    const host = canvas && canvas.parentElement
    if (!host || this._boxSelectInstalled) return
    this._boxSelectInstalled = true
    const adapterOf = () => (this.streaming && this.streaming.controller && this.streaming.controller.adapter)
      || (typeof window !== 'undefined' && window.__sl && window.__sl.streamingController && window.__sl.streamingController.adapter) || null
    let box = null   // { x0, y0, x1, y1 } in canvas pixels, and the overlay
    const local = (e) => { const r = canvas.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top, r } }
    // THE POINTER IS MEASURED IN ONE PIXEL SPACE AND `style` IS READ IN
    // ANOTHER. The tracking page renders with zoom 0.8 on the root, so a
    // clientX-derived distance of 30 was being written as style.left:30px
    // and rendering at 24 - the rectangle started 20% of the way back
    // toward the canvas origin, by an amount that GREW with distance from
    // it. Converting here, at the only place client numbers meet style,
    // rather than at each of the four assignments.
    const draw = () => {
      Object.assign(box.el.style, overlayRectStyle(box, cssPixelScale(canvas)))
    }
    // THE DRAG IS A STREAM, AND THE STREAM IS THROTTLED TO ONE FRAME.
    // A pointermove can fire several times per frame; projecting every
    // active instance that often is what would not survive SpaceX. The
    // rectangle itself still follows the pointer on every event - only
    // the SELECTION is recomputed once per frame, because that is the
    // expensive half.
    //
    // Computing the COUNT costs the same as computing the LIST: both walk
    // the active instances. Omitting identities mid-drag saves transport
    // and React state churn, not projection. The projection is made
    // affordable here, by the frame throttle.
    const liveTick = () => {
      box.raf = 0
      if (!box) return
      const r = canvas.getBoundingClientRect()
      let uids = []
      try { uids = uidsInScreenRect(adapterOf(), this.camera, box, { width: r.width, height: r.height }) } catch (err) { uids = [] }
      const next = new Set(uids)
      const { added, removed } = diffSets(box.painted, next)
      // Repaint only what crossed the edge of the rectangle this frame.
      if (added.length || removed.length) {
        try { this._paintBoxPreview(added, removed, box.preexisting) } catch (err) { /* the model still draws */ }
        box.painted = next
      }
      // Mid-drag the panel is told HOW MANY and not WHICH: a table cannot
      // be drawn for a selection that is still changing.
      box.publisher.publish(inProgressState(next.size, 'box'), { inProgress: true })
    }
    const onMove = (e) => {
      if (!box) return
      const p = local(e); box.x1 = p.x; box.y1 = p.y; draw()
      if (!box.raf && typeof requestAnimationFrame === 'function') box.raf = requestAnimationFrame(liveTick)
    }
    const end = (e) => {
      if (!box) return
      window.removeEventListener('pointermove', onMove, true)
      window.removeEventListener('pointerup', end, true)
      const b = box; box = null
      if (b.el && b.el.parentNode) b.el.parentNode.removeChild(b.el)
      if (this.cameraControls) this.cameraControls.enabled = b.controlsWere
      if (e) { const p = local(e); b.x1 = p.x; b.y1 = p.y }
      // A press without a drag is not a box.
      if (Math.abs(b.x1 - b.x0) < 4 || Math.abs(b.y1 - b.y0) < 4) return
      if (b.raf && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(b.raf)
      // The preview paint is the DRAG's, not the selection's: drop it
      // before the page applies the real highlight, or an element that
      // left the rectangle keeps a colour nothing owns.
      try { this._paintBoxPreview([], Array.from(b.painted), b.preexisting) } catch (err) { /* the model still draws */ }
      const r = canvas.getBoundingClientRect()
      let uids = []
      try { uids = uidsInScreenRect(adapterOf(), this.camera, b, { width: r.width, height: r.height }) } catch (err) { uids = [] }
      const api = this.ReactApi
      if (api && typeof api.boxSelect === 'function') {
        try { api.boxSelect(uids, { loaded: !!adapterOf() }) } catch (err) { /* the page's handler */ }
      }
      // AT RELEASE THE PANEL GETS FULL IDENTITY - status is assigned from
      // it and status acts on specific elements. `count` stays the TRUE
      // total even if the list were ever capped, so a consumer can say
      // "the first N of M" rather than quietly acting on N.
      b.publisher.publish(finalState(uids, 'box'), { inProgress: false })
    }
    host.addEventListener('pointerdown', (e) => {
      const api = this.ReactApi
      if (!api || typeof api.boxSelect !== 'function') return
      if (!(e.ctrlKey || e.metaKey) || e.button !== 0 || e.target !== canvas) return
      if (this.scene && (this.scene.measure || this.scene.clipping)) return
      if (this.pickPointHandler) return
      // Only in EMPTY space: a ctrl-press on a fragment stays the ctrl-click
      // that adds that one element.
      this._pointerNdc(e)
      this.raycaster.setFromCamera(this.mouse, this.camera)
      const hits = this.getRaycasterIntersects()
      if (hits && hits.length) return
      const p = local(e)
      const el = document.createElement('div')
      Object.assign(el.style, {
        position: 'absolute', pointerEvents: 'none', zIndex: 5,
        border: '1px dashed #2F6FED', background: 'rgba(47,111,237,0.10)',
      })
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative'
      host.appendChild(el)
      box = {
        x0: p.x, y0: p.y, x1: p.x, y1: p.y, el,
        controlsWere: this.cameraControls ? this.cameraControls.enabled : true,
        painted: new Set(), raf: 0,
        // Whatever was already highlighted when the drag began: the
        // preview never repaints it and never clears it.
        preexisting: (() => {
          const st = this.streaming && this.streaming.state
          const m = st && st.highlights
          return m && typeof m.keys === 'function' ? new Set(m.keys()) : new Set()
        })(),
        publisher: makePublisher((state, meta) => {
          const a = this.ReactApi
          if (a && typeof a.selectionChanged === 'function') a.selectionChanged(state, meta)
        }),
      }
      if (this.cameraControls) this.cameraControls.enabled = false
      draw()
      window.addEventListener('pointermove', onMove, true)
      window.addEventListener('pointerup', end, true)
    }, true)
  }

  // THE DRAG'S OWN PAINT, and it must not outlive the drag or steal a
  // colour that was already there.
  //
  // Per-instance colour is the cheap path - the store re-applies it on
  // every mesh build, so a tile that loads mid-drag arrives already
  // painted, and nothing re-uploads geometry. What is NOT cheap is
  // repainting the whole set each frame, which is why the caller hands
  // us only what crossed the rectangle's edge.
  //
  // A DELIBERATE COLOUR OUTRANKS A DERIVED ONE. An element that was
  // already highlighted before the drag began - selected earlier, lit by
  // a rule - keeps its colour: we neither repaint it nor clear it when
  // it leaves the rectangle. Without that, dragging a box across an
  // existing selection would quietly erase it, and the person would have
  // no way to know which colour was theirs.
  _paintBoxPreview(added, removed, keep) {
    const store = this.streaming && this.streaming.state;
    if (!store || typeof store.bulkHighlight !== 'function') return;   // legacy: no live preview
    const held = keep || new Set();
    const run = () => {
      // bulkClearHighlights emits ONCE for the whole set; clearing uid by
      // uid emits per element, which on a fast drag is the difference
      // between one notification a frame and hundreds. The status paint
      // beside this one already uses it.
      const drop = (removed || []).filter((uid) => !held.has(uid));     // held = not ours to clear
      if (drop.length) {
        try {
          if (typeof store.bulkClearHighlights === 'function') store.bulkClearHighlights(drop);
          else for (const uid of drop) store.clearHighlight(uid);
        } catch (e) { /* the model still draws */ }
      }
      const paint = (added || []).filter((uid) => !held.has(uid)).map((uid) => [uid, BOX_PREVIEW_COLOR]);
      if (paint.length) store.bulkHighlight(paint);
    };
    if (typeof store.withBatch === 'function') store.withBatch(run); else run();
  }

  checkMesh(uniqueId) {
    return this.meshesObject.has(uniqueId)
  }

  // The click handler's own index resolution, exposed so a diagnostic
  // reports what a click would do rather than re-deriving it.
  resolvePickIndex(hit) {
    return pickedIndex(hit)
  }

  onFindInJsonUsingViewer(uniqueId, isctrl) {
    this.ReactApi.findInJsonUsingViewer(uniqueId, isctrl)
  }

// addSelection — applies the selection highlight color to a uniqueId.
//
// Streaming-aware: when this.streaming?.state exists (set up by
// StreamingLoader), highlight is routed through the durable state
// store. The store remembers the color, and the InstanceStateApplicator
// re-applies it on every mesh build — so highlight survives:
//   - tile unload + reload (camera leaves frustum and returns)
//   - LOD swap (geometry pointer changes, instanceColor stays)
//   - tile boundary promotion (active slot changes between overlapping tiles)
//
// Legacy fallback: when streaming.state is absent (legacy non-streaming
// projects), uses the original highlightInstance path that writes
// directly to instanceColor. That path doesn't survive tile unload
// because legacy projects don't unload — every mesh stays in the scene
// from initial load.
//
// The non-instanced (regular Mesh) path stays unchanged in both cases.
// Select a list of elements at once, loaded or not (streaming only).
//
// A Category or Group row on an index-driven Project Files tree stands
// for elements whose records the browser has not read (SpaceX BA03, Apple
// RT06); the row-by-row path below only reaches elements present in
// meshesObject, so clicking such a row selected nothing and isolate had
// nothing to isolate (owner, 2026-09-19). The store holds the highlight
// for every uid and applies it as tiles arrive; each uid is recorded in
// the same prevMeshes bookkeeping a reset reverts, and totalSelection -
// what toggleIsolate isolates - carries them all.
selectUids(uids, selectors = {}, { add = false } = {}) {
  const store = this.streaming?.state;
  if (!store) return false;
  const { prevMeshes = 'prevMeshes', prevMaterials = 'prevMaterials', color, treeIndex = 0 } = selectors;
  if (!this.cachedMeshSelections.has(treeIndex)) this.cachedMeshSelections.set(treeIndex, []);
  if (!this[prevMeshes]) this[prevMeshes] = [];
  if (!this[prevMaterials]) this[prevMaterials] = [];
  store.beginBatch();
  try {
    if (!add) {
      for (let i = 0; i < this[prevMeshes].length; i++) {
        this._revertPrevMesh(this[prevMeshes][i], this[prevMaterials][i]?.materials || []);
      }
      this[prevMeshes] = [];
      this[prevMaterials] = [];
      this.totalSelection = [];
    }
    const have = new Set(this.totalSelection || []);
    const fresh = [];
    for (const uid of uids || []) {
      if (uid && !have.has(uid)) { have.add(uid); fresh.push(uid); }
    }
    store.bulkHighlight(fresh.map((uid) => [uid, color]));
    for (const uid of fresh) {
      this[prevMeshes].push({ parentNodeId: uid, meshes: [], instanceIndices: [] });
      this[prevMaterials].push({ parentNodeId: uid, materials: [] });
    }
    this.totalSelection = [...have];
    this.cachedMeshSelections.set(treeIndex, [...have]);
    this._syncIsolationToSelection();
  } finally {
    store.endBatch();
  }
  return true;
}

addSelection(neededMesh, color, uniqueId, jsonID, prevMeshes, prevMaterials) {
  if (!this[prevMeshes]) this[prevMeshes] = [];
  if (!this[prevMaterials]) this[prevMaterials] = [];

  const meshData = this.meshesObject.get(uniqueId);
  const streamingState = this.streaming?.state;

  if (
    meshData &&
    Array.isArray(meshData.fragments) &&
    meshData.fragments.length > 0 &&
    isSlotBacked(meshData.fragments[0].mesh)
  ) {
    const frags = meshData.fragments;

    if (streamingState) {
      // Store-routed: applicator finds slots via adapter and writes
      // the color. Color persists across rematerialization.
      streamingState.highlight(uniqueId, color);
    } else {
      // Legacy fallback: direct GPU write per slot.
      frags.forEach(({ mesh, index }) => {
        this.highlightInstance(mesh, index, color);
      });
    }

    this[prevMeshes].push({
      parentNodeId: uniqueId,
      meshes: frags.map(f => f.mesh),
      instanceIndices: frags.map(f => f.index),
    });
    this[prevMaterials].push({
      parentNodeId: uniqueId,
      materials: [],
    });

  } else if (meshData && isSlotBacked(meshData.mesh)) {
    const mesh = meshData.mesh;
    const instanceIndex = meshData.index;
    if (instanceIndex === undefined) return;

    this[prevMeshes].push({
      parentNodeId: uniqueId,
      meshes: [mesh],
      instanceIndex,
    });

    if (streamingState) {
      streamingState.highlight(uniqueId, color);
    } else {
      this.highlightInstance(mesh, instanceIndex, color);
    }

    this[prevMaterials].push({ parentNodeId: uniqueId, materials: [] });

  } else if (neededMesh) {
    // Non-instanced path — unchanged. Used for legacy GLB-loaded meshes
    // that are regular Mesh objects, not InstancedMesh.
    const meshes = GetChildrenMeshes(neededMesh);
    const material = getMaterialForColor(color);

    this[prevMeshes].push({ parentNodeId: uniqueId, meshes });
    this[prevMaterials].push({
      parentNodeId: uniqueId,
      materials: SetChildrenMaterial(neededMesh, material),
    });

  } else if (streamingState && hasParts(uniqueId)) {
    // A NESTED FAMILY: no slot of its own, only its parts (./nestedParts).
    // A hanger, a trapeze, a beam clamp is drawn entirely as rods, nuts and
    // struts under their own uids, so meshesObject has nothing for the
    // ELEMENT and this used to fall to the bare return below - the click
    // resolved, nothing lit, totalSelection never moved (owner via Tom,
    // 2026-10-15). The highlight goes on the ELEMENT, which stays the
    // selected id for the tree, the panel and any status write; the store
    // answers each part's colour from it (getHighlight), the applicator
    // repaints the parts already drawn, and a part streaming in later is
    // composed with it as its tile lands. Recorded in the multi-fragment
    // shape so _revertPrevMesh clears it through the store like any other.
    streamingState.highlight(uniqueId, color);
    this[prevMeshes].push({ parentNodeId: uniqueId, meshes: [], instanceIndices: [] });
    this[prevMaterials].push({ parentNodeId: uniqueId, materials: [] });

  } else {
    return;
  }

  this.totalSelection.push(uniqueId);
  if (jsonID !== undefined) this.totaljsonIds.push(jsonID);
}

  highlightInstance(instancedMesh, instanceIndex, color) {
    if (!instancedMesh || !instancedMesh.isInstancedMesh) return;
    if (instanceIndex === undefined || instanceIndex === null || instanceIndex < 0) return;
    if (instanceIndex >= instancedMesh.count) return;
    
    if (!instancedMesh.instanceColor) {
      const colors = new Float32Array(instancedMesh.count * 3);
      for (let i = 0; i < instancedMesh.count; i++) {
        colors[i * 3] = 1;
        colors[i * 3 + 1] = 1;
        colors[i * 3 + 2] = 1;
      }
      instancedMesh.instanceColor = new InstancedBufferAttribute(colors, 3);
    }
    
    if (!instancedMesh.userData.originalInstanceColors) {
      instancedMesh.userData.originalInstanceColors = new Float32Array(instancedMesh.count * 3);
      for (let i = 0; i < instancedMesh.count; i++) {
        const c = new Color();
        instancedMesh.getColorAt(i, c);
        c.toArray(instancedMesh.userData.originalInstanceColors, i * 3);
      }
    }
    
    const highlightColor = new Color(color);
    instancedMesh.setColorAt(instanceIndex, highlightColor);
    instancedMesh.instanceColor.needsUpdate = true;
  }

  unhighlightInstance(instancedMesh, instanceIndex) {
    if (!instancedMesh || !instancedMesh.isInstancedMesh) return;
    if (instanceIndex === undefined || instanceIndex === null || instanceIndex < 0) return;
    if (instanceIndex >= instancedMesh.count) return;
    
    if (instancedMesh.userData.originalInstanceColors && instancedMesh.instanceColor) {
      const originalColor = new Color();
      originalColor.fromArray(instancedMesh.userData.originalInstanceColors, instanceIndex * 3);
      instancedMesh.setColorAt(instanceIndex, originalColor);
      instancedMesh.instanceColor.needsUpdate = true;
    }
  }

  applyInstancedMeshVisibility(instancedMesh, visibleInstances) {
    if (!instancedMesh || !instancedMesh.isInstancedMesh) return;
    if (!this.isCentered) return;
    if (!instancedMesh.userData.centeredMatrices) return;
    
    const visibleSet = new Set(visibleInstances);
    const matrix = new Matrix4();
    const position = new Vector3();
    const quaternion = new Quaternion();
    const scale = new Vector3();
    const zeroScale = new Vector3(0.0001, 0.0001, 0.0001);
    
    const centeredMatrices = instancedMesh.userData.centeredMatrices;
    
    for (let i = 0; i < instancedMesh.count; i++) {
      matrix.fromArray(centeredMatrices, i * 16);
      
      if (!visibleSet.has(i)) {
        matrix.decompose(position, quaternion, scale);
        matrix.compose(position, quaternion, zeroScale);
      }
      
      instancedMesh.setMatrixAt(i, matrix);
    }
    
    instancedMesh.instanceMatrix.needsUpdate = true;
  }

  resetInstancedMeshVisibility(instancedMesh) {
    if (!instancedMesh || !instancedMesh.isInstancedMesh) return;
    if (!instancedMesh.userData.centeredMatrices) return;
    
    const matrix = new Matrix4();
    const centeredMatrices = instancedMesh.userData.centeredMatrices;
    
    for (let i = 0; i < instancedMesh.count; i++) {
      matrix.fromArray(centeredMatrices, i * 16);
      instancedMesh.setMatrixAt(i, matrix);
    }
    
    instancedMesh.instanceMatrix.needsUpdate = true;
  }

// clearAllSelections — wrap the per-uid revert loop in a store batch
// when streaming is active. _revertPrevMesh routes through
// streamingState.clearHighlight per uid; without the batch that's N
// emits → N applicator passes for an N-uid selection. With the batch,
// all clearHighlight emits coalesce into ONE applicator pass.
//
// beginBatch/endBatch are inert when streaming.state isn't set
// (legacy non-streaming projects), so this is unconditionally safe.
// try/finally guarantees endBatch runs even if a revert throws.
clearAllSelections(prevMeshes, prevMaterials) {
  if (!this[prevMeshes]) this[prevMeshes] = [];
  if (!this[prevMaterials]) this[prevMaterials] = [];

  const store = this.streaming?.state;
  if (store) store.beginBatch();
  try {
    for (let i = 0, len = this[prevMeshes].length; i < len; i++) {
      const prevMesh = this[prevMeshes][i];
      const materials = this[prevMaterials][i]?.materials || [];
      this._revertPrevMesh(prevMesh, materials);
    }
  } finally {
    if (store) store.endBatch();
  }

  this[prevMeshes] = [];
  this[prevMaterials] = [];
  this.totalSelection = [];
  this.totaljsonIds = [];
}

// While isolate mode is on, the visible set should track the user's
// selection: ctrl-extending selection extends the isolated visible
// group, ctrl-deselecting shrinks it, replacing the selection (plain
// click) replaces the isolated set. Without this, a ctrl-click hits
// the 'reset' inside selectElements, which used to call toggleIsolate
// and dump everything back into view — the symptom users hit as
// "everything shows up at once on ctrl-click in isolate mode."
//
// We keep the streaming store's `isolated` set authoritative for
// visibility composition (isEffectivelyVisible) and just push the
// current selection into it here. isolateMode (the viewer-side flag)
// is untouched — toggling isolate off is still the single explicit
// action to leave isolate mode. setIsolation([]) collapses to
// clearIsolation inside the store, but isolateMode stays true, so the
// next selection change re-establishes isolation around the new
// selection.
//
// Source of truth for "current selection" mirrors toggleIsolate's
// collection logic: union of cachedMeshSelections across every tree
// plus totalSelection. The two layers exist because Socket vs non-
// Socket selection paths populate them differently — onClickChildInList
// pushes into totalSelection, onClickChildInListSocket pushes into
// cachedMeshSelections per-tree — and the only place that reconciles
// the two is toggleIsolate. Doing the same reconciliation here keeps
// the isolated set consistent with what an explicit toggleIsolate
// would have selected.
_syncIsolationToSelection() {
  if (!this.isolateMode) return;
  const store = this.streaming?.state;
  if (!store) return;

  const all = new Set();
  // Five trees in the wild: matched, unmatched, existing, demolition,
  // disabled. Iterate up to 5 to stay forward-compatible if more get
  // added; the get() returns undefined for missing slots.
  for (let i = 0; i < 5; i++) {
    const cache =
      this.cachedMeshSelections.get(i) ||
      this.cachedMeshSelections.get(`${i}`);
    if (cache && cache.length > 0) cache.forEach(id => all.add(id));
  }
  if (this.totalSelection && this.totalSelection.length > 0) {
    this.totalSelection.forEach(id => all.add(id));
  }

  // THE FAMILY, as toggleIsolate does: isolating a hanger by its own uid
  // alone hid every rod and nut it is drawn as, which is all of it.
  store.setIsolation(expandToFamily([...all]));
}

onClickChildInListSocket(uniqueId, keyIsCtrl, selectors, action, sendWebsocket, jsonID) {
  const neededMesh = this.meshesObject.get(uniqueId);
  const { prevMeshes, prevMaterials, color, treeIndex } = selectors;

  const numericTreeIndex = typeof treeIndex === 'string' ? parseInt(treeIndex) : treeIndex;

  if (!this.cachedMeshSelections.has(numericTreeIndex)) {
    this.cachedMeshSelections.set(numericTreeIndex, []);
  }

  // Lazily initialize per-tree prevMeshes/prevMaterials slots. The
  // constructor seeds the four legacy trees (matched, unmatched,
  // existing, demolition, files) but NOT every newer tree (e.g.
  // 'prevDisabledMeshes'). When SocketViewer's clearOtherTreesLocal
  // routes a 'reset' through here for a tree whose slot was never
  // touched, `this[prevMeshes]` reads as undefined and `.length` throws
  // — that surface-level "viewer crashes on click" symptom is exactly
  // this. Mirroring the cachedMeshSelections lazy-init keeps every
  // selector self-bootstrapping.
  //
  // Note the existing typo in the constructor: `prevExisitingMaterials`
  // (sic) was created instead of `prevExistingMaterials`. Selectors use
  // the correct spelling, which would otherwise hit the same undefined
  // path. Lazy-init covers that too without renaming the legacy field.
  if (!this[prevMeshes])    this[prevMeshes]    = [];
  if (!this[prevMaterials]) this[prevMaterials] = [];

  const meshIndex = this[prevMeshes].findIndex(m => m.parentNodeId === uniqueId);
  const hasMesh = meshIndex !== -1;

  switch (action) {
    case 'reset': {
      for (let i = 0; i < this[prevMeshes].length; i++) {
        const prevMesh = this[prevMeshes][i];
        const materials = this[prevMaterials][i]?.materials || [];
        this._revertPrevMesh(prevMesh, materials);
      }

      this[prevMeshes] = [];
      this[prevMaterials] = [];
      this.totalSelection = [];
      this.totaljsonIds = [];
      this.cachedMeshSelections.set(numericTreeIndex, []);

      this[prevMeshes].push({ parentNodeId: '', meshes: [] });
      this[prevMaterials].push({ parentNodeId: '', materials: [] });

      // Don't auto-toggleIsolate here. selectElements (in TreeViewerItem)
      // routes a 'reset' BEFORE every re-add inside the same batch — if
      // we tore down isolation here, a ctrl-click extension would see
      // its isolated set vanish and "everything shows up at once" until
      // the re-adds rebuild it (or it would never rebuild for the rest
      // of this branch). Instead, treat isolation as derived from
      // selection while isolateMode is on: _syncIsolationToSelection
      // below pushes the post-mutation totalSelection into the store's
      // isolated set. Reset → empty selection → empty isolated set,
      // which the store flattens to clearIsolation; the next re-add
      // brings isolation back around the new selection.
      break;
    }

    case 'deselect': {
      if (!hasMesh) break;

      const prevMesh = this[prevMeshes][meshIndex];
      const materials = this[prevMaterials][meshIndex]?.materials || [];
      this._revertPrevMesh(prevMesh, materials);

      this[prevMeshes].splice(meshIndex, 1);
      this[prevMaterials].splice(meshIndex, 1);

      const cached = this.cachedMeshSelections.get(numericTreeIndex) || [];
      const selIndex = cached.indexOf(uniqueId);
      if (selIndex > -1) {
        cached.splice(selIndex, 1);
        this.cachedMeshSelections.set(numericTreeIndex, cached);
      }

      const totalIndex = this.totalSelection.indexOf(uniqueId);
      if (totalIndex > -1) this.totalSelection.splice(totalIndex, 1);

      const jsonIndex = this.totaljsonIds.indexOf(jsonID);
      if (jsonIndex > -1) this.totaljsonIds.splice(jsonIndex, 1);

      // No re-isolate dance. With ViewerStateStore's clean composition
      // (isolate set independent of hide/highlight), removing a uid
      // from the selection does NOT need isolate to flip off-and-on.
      // The isolate set stays as-is; the uid's highlight is already
      // cleared by _revertPrevMesh above. Selection and isolate live
      // independently.
      break;
    }

    case 'select': {
      if (keyIsCtrl) {
        if (hasMesh) {
          const prevMesh = this[prevMeshes][meshIndex];
          const materials = this[prevMaterials][meshIndex]?.materials || [];
          this._revertPrevMesh(prevMesh, materials);

          this[prevMeshes].splice(meshIndex, 1);
          this[prevMaterials].splice(meshIndex, 1);

          const cached = this.cachedMeshSelections.get(numericTreeIndex) || [];
          const selIndex = cached.indexOf(uniqueId);
          if (selIndex > -1) {
            cached.splice(selIndex, 1);
            this.cachedMeshSelections.set(numericTreeIndex, cached);
          }

          const totalIndex = this.totalSelection.indexOf(uniqueId);
          if (totalIndex > -1) this.totalSelection.splice(totalIndex, 1);

          const jsonIndex = this.totaljsonIds.indexOf(jsonID);
          if (jsonIndex > -1) this.totaljsonIds.splice(jsonIndex, 1);

          // No re-isolate dance — see deselect branch.
        } else {
          // No wasInIsolateMode dance. addSelection routes through
          // streamingState.highlight when streaming is active; the
          // store remembers the highlight color, applicator writes
          // it correctly even when the uid is currently isolate-
          // hidden (it'll appear with the right color when un-
          // isolated). Toggling isolate off and on around the
          // addSelection was a workaround for the legacy direct-
          // matrix path losing track of state.
          this.addSelection(neededMesh, color, uniqueId, jsonID, prevMeshes, prevMaterials);

          const cached = this.cachedMeshSelections.get(numericTreeIndex) || [];
          if (!cached.includes(uniqueId)) {
            cached.push(uniqueId);
            this.cachedMeshSelections.set(numericTreeIndex, cached);
          }
        }
      } else {
        // Non-ctrl click: replace the entire highlighted selection
        // with this single uid. Don't touch isolate state — if the
        // user was in isolate mode, they stay in isolate mode; the
        // newly-selected uid (if it's in the isolate set) shows
        // with its highlight color, otherwise stays hidden by
        // isolate as it should be.
        this.clearAllSelections(prevMeshes, prevMaterials);
        this.addSelection(neededMesh, color, uniqueId, jsonID, prevMeshes, prevMaterials);

        this.cachedMeshSelections.set(numericTreeIndex, [uniqueId]);
      }
      break;
    }
  }

    // === PER-TREE SELECTION, ACTUALLY PER TREE ===
    //
    // This used to set EVERY tree's slot to the same totaljsonIds ARRAY
    // REFERENCE, so the map looked per-tree and was not - reading it back
    // and concatenating produced the same ids up to five times. Nothing
    // depended on it being right, so nothing caught it.
    //
    // The selection-sync contract (desktop agent, 2026-09-27) needs the real
    // thing: every change sends the FULL set GROUPED BY TREE,
    // [treeIdx, id, id, treeIdx, id...], and the receiver REPLACES. A flat
    // cumulative list cannot be grouped after the fact, because nothing
    // records which tree an id came from - so it is recorded here, at the
    // only point that knows.
    //
    // Incremental rather than rebuilt: the only ids that change are the ones
    // just clicked, and they belong to numericTreeIndex by construction. A
    // plain click has already emptied totaljsonIds, so everything in it is
    // this tree's.
    if (!(this._selByTree instanceof Map)) this._selByTree = new Map();
    if (action === 'reset' || !keyIsCtrl) {
      this._selByTree.clear();
      if (this.totaljsonIds.length) this._selByTree.set(numericTreeIndex, new Set(this.totaljsonIds));
    } else {
      let set = this._selByTree.get(numericTreeIndex);
      if (!set) { set = new Set(); this._selByTree.set(numericTreeIndex, set); }
      if (action === 'deselect') set.delete(jsonID);
      else if (jsonID !== undefined) set.add(jsonID);
      if (!set.size) this._selByTree.delete(numericTreeIndex);
    }
  this.cachedSelection.set(numericTreeIndex, this.totaljsonIds);

  // Push the post-mutation selection into the streaming store's
  // isolated set when isolate mode is on. Inert when isolate is off,
  // so this is safe to call after every action ('reset', 'select',
  // 'deselect') unconditionally. Inside selectElements (TreeViewerItem)
  // every onClickChildInList call is wrapped in store.beginBatch /
  // endBatch, so the chain of "reset → empty isolation → re-add → grow
  // isolation" coalesces into a single applicator full-sweep at the
  // end of the batch. No flicker.
  this._syncIsolationToSelection();

  if (!sendWebsocket && action === 'select' && this.totalSelection.length > 0) {
    this.targetItems();
  }

  if (sendWebsocket && this.ReactApi && this.ReactApi.setPayload) {
    // Emit the right verb so receivers preserve the "single tree at a
    // time, unless ctrl" rule. The 'click' handler on receivers does a
    // multi-tree clear when arr.length > 1; that's correct for plain
    // clicks but wrong for ctrl-extends, which must NOT touch sibling
    // trees. Routing ctrl-clicks through 'ctrlclick' keeps that rule
    // intact end-to-end.
    //
    // Plain click: send the cumulative selection across all trees so a
    //   receiver re-creates the whole selection state in one shot
    //   (clear everything else, reset this tree, then re-select). This
    //   matches the existing protocol — don't change it.
    //
    // Ctrl click: send ONLY the uid the user just toggled, paired with
    //   its tree index. The 'ctrlclick' handler walks the message and
    //   calls selectClick(..., isCtrl=true) which is a per-uid TOGGLE
    //   (deselects if already selected, selects otherwise) — exactly
    //   the semantics of a single ctrl-click. Sending the cumulative
    //   selection here would re-toggle every previously-selected uid
    //   and flip them all off.
    // === ONE VERB: THE FULL SET, GROUPED BY TREE ===
    //
    // Contract agreed with the desktop agent, 2026-09-27. Every selection
    // change on either side sends its FULL post-change set as one 'sync';
    // the receiver REPLACES and never re-emits. click and ctrlclick are gone
    // from this path.
    //
    // WHY THE OLD ctrlclick SENT ONE ID: the receiver's ctrlclick handler is
    // a per-uid TOGGLE, so sending the cumulative set would have re-toggled
    // everything already selected and flipped it all off. Under replace
    // semantics that problem disappears - which is the argument for the
    // contract rather than for patching the toggle.
    //
    // ROLLOUT, AND WHY THIS MUST NOT SHIP HALF-DONE: C# stops obeying
    // click/ctrlclick/deselect the moment it sees ANY message stamped
    // origin 'viewer'. So the stamp and this emit belong in the SAME BUNDLE -
    // stamping while the canvas still sent ctrlclick would make C# drop
    // every canvas ctrl-click.
    const grouped = [];
    (this._selByTree instanceof Map ? this._selByTree : new Map()).forEach((ids, treeIdx) => {
      if (!ids || !ids.size) return;
      grouped.push(treeIdx);
      ids.forEach((id) => grouped.push(id));
    });
    this._selSeq = (this._selSeq || 0) + 1;
    this.ReactApi.setPayload(['sync', JSON.stringify(grouped), this._selSeq, 'viewer']);
  }
}

onClickChildInList(uniqueId, keyIsCtrl, selectors, unpriced, shouldFrameCamera) {
  const neededMesh = this.meshesObject.get(uniqueId);
  // THE OTHER DIRECTION: the tree asking the model to light something up.
  const _ccl = {
    uniqueId: String(uniqueId || '').slice(0, 48),
    meshFound: !!neededMesh,
    ctrl: !!keyIsCtrl,
    meshesKnown: this.meshesObject ? this.meshesObject.size : 0,
  };
  console.log('[trace onClickChildInList]', _ccl);
  reportViewerTrace('trace/3d-onClickChildInList', _ccl);
  const { prevMeshes, prevMaterials, color, treeIndex } = selectors;
  // An element drawn only as its nested parts has no mesh of its own and is
  // still a real selection - see addSelection. Without this the guard below
  // returned silently for every hanger, trapeze and beam clamp.
  const familyOnly = !neededMesh && uniqueId !== 'reset' && !!this.streaming?.state && hasParts(uniqueId);

  if (!this.cachedMeshSelections.has(treeIndex)) {
    this.cachedMeshSelections.set(treeIndex, []);
  }

  // Same lazy-init guard as onClickChildInListSocket — prevents
  // `this[prevMeshes].length` from throwing on selectors whose backing
  // field wasn't pre-seeded by the constructor (e.g. newer trees, or
  // the `prevExistingMaterials` typo collision).
  if (!this[prevMeshes])    this[prevMeshes]    = [];
  if (!this[prevMaterials]) this[prevMaterials] = [];

  if (!neededMesh && !familyOnly) {
    if (uniqueId !== 'reset') {
      // The branch that returns silently: no mesh resident for this uid, and
      // it is not a nested family either. Said out loud now.
      console.log('[trace onClickChildInList] no mesh and no family -> returning', { uniqueId: String(uniqueId || '').slice(0, 48) });
      reportViewerTrace('trace/3d-no-mesh', { uniqueId: String(uniqueId || '').slice(0, 48) });
    }
    if (uniqueId === 'reset') {
      for (let i = 0, len = this[prevMeshes].length; i < len; i++) {
        const prevMesh = this[prevMeshes][i];
        const materials = this[prevMaterials][i]?.materials || [];
        this._revertPrevMesh(prevMesh, materials);
      }

      this[prevMeshes] = [];
      this[prevMaterials] = [];
      this.totalSelection = [];
      this.cachedMeshSelections.set(treeIndex, []);

      this[prevMeshes].push({ parentNodeId: '', meshes: [] });
      this[prevMaterials].push({ parentNodeId: '', materials: [] });

      // Don't auto-toggleIsolate here. selectElements routes a 'reset'
      // BEFORE every re-add inside the same batch — tearing down
      // isolation here would dump every uid back into view ("everything
      // shows up at once on ctrl-click in isolate mode"). Sync the
      // (now empty) selection into the store's isolated set instead;
      // the re-adds that follow inside the batch grow it back around
      // the new selection. See _syncIsolationToSelection.
      this._syncIsolationToSelection();
    }
    return;
  }

  const meshIndex = this[prevMeshes].findIndex(m => m.parentNodeId === uniqueId);
  const hasMesh = meshIndex !== -1;

  const cached = this.cachedMeshSelections.get(treeIndex) || [];

  if (keyIsCtrl) {
    if (hasMesh) {
      const prevMesh = this[prevMeshes][meshIndex];
      const materials = this[prevMaterials][meshIndex]?.materials || [];
      this._revertPrevMesh(prevMesh, materials);

      this[prevMeshes].splice(meshIndex, 1);
      this[prevMaterials].splice(meshIndex, 1);

      const cachedIndex = cached.indexOf(uniqueId);
      if (cachedIndex > -1) cached.splice(cachedIndex, 1);

      const totalIndex = this.totalSelection.indexOf(uniqueId);
      if (totalIndex > -1) this.totalSelection.splice(totalIndex, 1);

      // No re-isolate dance. Selection and isolate are independent
      // dimensions; toggling a uid's highlight off doesn't require
      // isolate to flip. See onClickChildInListSocket for the same
      // reasoning.
    } else {
      // No wasInIsolateMode save/restore. addSelection routes
      // through streamingState.highlight when streaming is active;
      // the store handles the visibility composition.
      this.addSelection(neededMesh, color, uniqueId, undefined, prevMeshes, prevMaterials);

      if (!cached.includes(uniqueId)) cached.push(uniqueId);
      if (!this.totalSelection.includes(uniqueId)) this.totalSelection.push(uniqueId);
    }
  } else {
    // Non-ctrl click: replace selection wholesale. Don't toggle
    // isolate. If a user was viewing isolated geometry and clicks
    // a single tree row, they stay isolated — the newly-selected
    // uid joins (or stays out of, depending on whether it's in
    // the isolate set) the visible group, and gets the highlight
    // color either way.
    const store = this.streaming?.state;
    if (store) store.beginBatch();
    try {
      for (let i = 0, len = this[prevMeshes].length; i < len; i++) {
        const prevMesh = this[prevMeshes][i];
        const materials = this[prevMaterials][i]?.materials || [];
        this._revertPrevMesh(prevMesh, materials);
      }

      cached.length = 0;
      cached.push(uniqueId);

      this.totalSelection.length = 0;
      this.totalSelection.push(uniqueId);

      this[prevMeshes] = [];
      this[prevMaterials] = [];
      this.addSelection(neededMesh, color, uniqueId, undefined, prevMeshes, prevMaterials);
    } finally {
      if (store) store.endBatch();
    }
  }

  this.cachedMeshSelections.set(treeIndex, cached);

  // Push the post-mutation selection into the streaming store's
  // isolated set when isolate mode is on. Same role as the matching
  // call in onClickChildInListSocket — coalesces inside selectElements'
  // batch so a ctrl-extend rebuilds isolation around the new selection
  // without flicker.
  this._syncIsolationToSelection();

  if (shouldFrameCamera && this.totalSelection.length > 0) {
    this.targetItems();
  }
}

// Bulk version of the WS-'click' per-item loop. Replaces N invocations
// of onClickChildInListSocket with ONE pass that aggregates state and
// finally calls store.bulkHighlight — which emits a single 'highlight'
// event covering all uids, triggering ONE applyForUids in the applicator
// instead of N. Used by SocketViewer.jsx 'click' case when the inbound
// jsonId batch is large (e.g., 4000-element shell selection through C#).
//
// Streaming-only path. For legacy non-streaming projects, fall back to
// the per-item path — they don't have the bulkHighlight optimisation
// available and the per-item cost is already O(1) without an applicator.
bulkSelectSocket(pairs, selectors, options) {
  // options.skipClear=true: ctrl-extend behavior — do NOT wipe within-tree
  // state first. Default false matches the plain-click case which replaces
  // the current selection in the tree.
  const skipClear = options && options.skipClear;
  const { prevMeshes, prevMaterials, color, treeIndex } = selectors;
  const numericTreeIndex = typeof treeIndex === 'string' ? parseInt(treeIndex) : treeIndex;

  if (!this.cachedMeshSelections.has(numericTreeIndex)) {
    this.cachedMeshSelections.set(numericTreeIndex, []);
  }
  if (!this[prevMeshes]) this[prevMeshes] = [];
  if (!this[prevMaterials]) this[prevMaterials] = [];

  const streamingState = this.streaming?.state;
  if (!streamingState) {
    // Legacy fallback — per-item is fine when there's no applicator to
    // be the bottleneck. Caller should detect this and use selectClick
    // instead, but guard here as a safety net.
    for (const { uid, jsonId } of pairs) {
      this.onClickChildInListSocket(uid, true, selectors, 'select', false, jsonId);
    }
    return;
  }

  // Wipe within-tree state first (replicates the non-ctrl reset that
  // selectClick fires on the FIRST item in the per-item path). Uses the
  // store's batching so the wipe + the bulk-highlight that follows
  // coalesce into a single applicator pass. For ctrl-extend (skipClear),
  // we keep existing selection and just add to it.
  streamingState.beginBatch();
  try {
    if (!skipClear) {
      this.clearAllSelections(prevMeshes, prevMaterials);
    }

    const colorObj = new Color(color);
    const entries = [];  // [uid, color] pairs for bulkHighlight
    const cached  = [];  // new cachedMeshSelections[treeIndex]

    for (const { uid, jsonId } of pairs) {
      const meshData = this.meshesObject.get(uid);
      if (!meshData) continue;

      // Mirror addSelection's prevMeshes / prevMaterials bookkeeping so
      // a subsequent deselect or reset can find these slots to revert.
      if (Array.isArray(meshData.fragments) && meshData.fragments.length > 0
          && isSlotBacked(meshData.fragments[0].mesh)) {
        const frags = meshData.fragments;
        this[prevMeshes].push({
          parentNodeId: uid,
          meshes: frags.map(f => f.mesh),
          instanceIndices: frags.map(f => f.index),
        });
        this[prevMaterials].push({ parentNodeId: uid, materials: [] });
      } else if (isSlotBacked(meshData.mesh)) {
        const idx = meshData.index;
        if (idx === undefined) continue;
        this[prevMeshes].push({
          parentNodeId: uid,
          meshes: [meshData.mesh],
          instanceIndex: idx,
        });
        this[prevMaterials].push({ parentNodeId: uid, materials: [] });
      } else {
        // Non-instanced mesh in a streaming project — rare, skip.
        continue;
      }

      entries.push([uid, colorObj]);
      cached.push(uid);
      this.totalSelection.push(uid);
      if (jsonId !== undefined) this.totaljsonIds.push(jsonId);
    }

    if (entries.length > 0) streamingState.bulkHighlight(entries);

    this.cachedMeshSelections.set(numericTreeIndex, cached);
    this.cachedSelection.set(numericTreeIndex, this.totaljsonIds);
  } finally {
    streamingState.endBatch();
  }

  // Single isolation sync at the very end (no-op when isolateMode is off).
  this._syncIsolationToSelection();

  if (this.totalSelection.length > 0) {
    this.targetItems();
  }
}

  // Normalised device coordinates for a pointer event, measured against
  // the CANVAS's own box.
  //
  // This used to read e.layerX / e.layerY over the container's offset
  // size. Both halves of that are wrong in ways that only show up once
  // the viewer stops being the only thing on the page:
  //
  //   layerX/layerY is non-standard and is measured from the nearest
  //   POSITIONED ancestor - so a rail that becomes position:relative, a
  //   resizable split moving the pane, or the page scrolling silently
  //   moves the origin, and the ray stops pointing where the cursor is.
  //   That is the "highlight lands next to the thing I am hovering"
  //   offset, and it appears suddenly because it takes a layout change,
  //   not a viewer change, to trigger it.
  //
  //   container.offsetWidth includes the container's padding and border,
  //   and it can differ from the canvas's displayed size between a
  //   layout change and the next resize - a scaling error on top of the
  //   translation one, worst at the edges.
  //
  // getBoundingClientRect on the canvas is immune to all of it: scroll,
  // ancestors, padding, device pixel ratio, CSS scaling.
  _pointerNdc(e, out = this.mouse) {
    const el = this.renderer && this.renderer.domElement;
    const rect = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null;
    if (!rect || !rect.width || !rect.height) {
      out.x = 0;
      out.y = 0;
      return out;
    }
    out.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    out.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    return out;
  }

  // ONE PLACE THAT SAYS 'THAT CLICK SELECTED NOTHING', so the two callers - a
  // ray that missed everything, and a hit the picker cannot map to an element -
  // cannot drift apart. Both mean the same thing to the page.
  //
  // The ctrl flag is passed through UNCHANGED even though a miss clears either
  // way: the page decides what a miss means, not the viewer, and swallowing the
  // modifier here would leave it unable to tell them apart later.
  _reportPickMiss(e) {
    // BOTH HALVES, or the clear is not a clear.
    //
    // Owner, 2026-09-30: "keeps some elements selected, needs fixing". There are
    // TWO selections here and they are separate by design - the page paints its
    // picks through highlightUids and they never reach the viewer's own
    // totalSelection (ModelStage says so, and the 2026-09-22 headless run
    // measured it: a box of 687 with totalSelection 0). Clearing only the page
    // half leaves whatever the VIEWER selected still lit.
    //
    // Hide already solved this exact problem the same way further down this file:
    // clear the viewer's half, then let the page drop its own.
    try { this.clearAllSelections("prevMeshes", "prevMaterials") } catch (err) { /* nothing selected here */ }
    const api = this.ReactApi
    if (!api || typeof api.findInJsonUsingViewer !== 'function') return
    try { api.findInJsonUsingViewer(null, !!(e && e.ctrlKey)) } catch (err) { /* the page's handler */ }
  }

  // Where a pointer event sits INSIDE the canvas, in CSS pixels - for
  // positioning DOM overlays (the price popover) over the hit point.
  _pointerLocal(e) {
    const el = this.renderer && this.renderer.domElement;
    const rect = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null;
    if (!rect) return { x: 0, y: 0 };
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  getRaycasterIntersects() {
    let intersects = undefined
    let raycastMeshes = [...this.meshes, ...this.clippingHelpers]

    if (!this.scene.clipping) {
      raycastMeshes = raycastMeshes.filter(mesh => mesh.name != 'clipingHelper')
    }

    intersects = this.raycaster.intersectObjects(raycastMeshes.filter(mesh => mesh.visible))
    // Hidden, released and not-yet-shown items of a pool or tile batch sit
    // at a collapsed matrix (scale 0.0001 at the origin) and the raycaster
    // still hits them: the nearest "hit" was then a hidden item at (0,0,0),
    // which is why measuring gave 0 / NaN (owner, 2026-09-18). Drop them.
    // ...and a hit at the ray's own origin is geometry around the camera,
    // not what the pointer is on.
    intersects = intersects.filter(h => !collapsedItemHit(h) && !(h.distance < 1e-3))
    
    intersects = this.clipper.clippingPlanes.length === 0
      ? intersects
      : intersects.filter(intersectMesh => {
          let meshVisible = true
          if (intersectMesh.object.name == 'clipingHelper') return true
          for (const clipPlane of this.clipper.clippingPlanes) {
            if (clipPlane.distanceToPoint(intersectMesh.point) < 0) {
              meshVisible = false
              break
            }
          }
          return meshVisible
        })

    return intersects
  }

  onTweenCamera(targetPosition) {
    this.cameraControls.enabled = false
    this.OrbitControls.enabled = true
    const theta = this.angleBetweenTwoVectors(this.camera.position, targetPosition)
    const x = targetPosition.x * Math.cos(theta) + this.camera.position.z * Math.sin(theta)
    const y = Math.abs(targetPosition.y * Math.cos(theta) + this.camera.position.z * Math.cos(theta) + 1)
    const z = targetPosition.z * Math.cos(theta) - this.camera.position.x * Math.sin(theta)
    const cameraFinalPosition = new Vector3(x, y, z)
    
    new TWEEN.Tween(this.camera.position).to(cameraFinalPosition, 1000).start()
    new TWEEN.Tween(this.OrbitControls.target).to(targetPosition, 1000).start()
    
    this.OrbitControls.enabled = false
    this.cameraControls.enabled = true
  }

  angleBetweenTwoVectors(a, b) {
    return MathUtils.radToDeg(Math.atan2(a.y - b.y, a.x - b.x))
  }

  init() {
    this.vertexShader = /*glsl*/`
      varying vec3 vWorldPosition;
      void main() {
        vec4 worldPosition = modelMatrix * vec4( position, 1.0 );
        vWorldPosition = worldPosition.xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
      }
    `;
    
    this.fragmentShader = /*glsl*/`
      uniform vec3 topColor;
      uniform vec3 bottomColor;
      uniform float offset;
      uniform float exponent;
      varying vec3 vWorldPosition;
      void main() {
        float h = normalize( vWorldPosition + offset ).y;
        gl_FragColor = vec4( mix( bottomColor, topColor, max( pow( max( h , 0.0), exponent ), 0.0 ) ), 1.0 );
      }
    `;

    this.raycaster.layers.set(0)

    this.camera = new PerspectiveCamera(
      60,
      this.container.offsetWidth / this.container.offsetHeight,
      0.05,
      600000
    )
    this.camera.position.set(0, 0, 0)
    this.camera.frustumCulled = true

    this.viewerHelper = new ViewHelper(this.camera, this.container, this.socketviewer);

     // ADD THIS: Create ViewHelper overlay renderer
  // MATCHING powerPreference ON PURPOSE. This is a SECOND WebGL context
  // on the same page; if the main one asks for the discrete GPU and this
  // one takes the default (or asks for low-power), a switchable-graphics
  // machine can end up running BOTH adapters at once to serve one page -
  // which costs more than the 150x150 cube it draws. Same preference,
  // one GPU.
  this.viewHelperRenderer = new WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'high-performance' });
  this.viewHelperRenderer.setSize(150, 150);
  // BOTTOM LEFT, where it has always been. The owner has now seen it in
  // the other corner and wants it back; his call, and that ends it
  // (2026-09-20, via the leader who had asked for the move).
  //
  // Keeping the reasoning for the move, because it was not wrong about
  // the SYMPTOM and someone will propose it again: he reported the cube
  // "not visible or not working", and it has never been clickable -
  // pointerEvents none below, and no click handling at all in view-helper
  // (183 lines, no listener, no raycaster). That half is real, and moving
  // the cube never addressed it. Making it a control rather than an
  // indicator is separate work that nobody has asked for yet.
  //
  // The move also rested on it sitting "under the view buttons", which
  // holds on ONE of the nine pages the strip is mounted on: six put the
  // strip bottom-left or top-left, so there the move simply placed the
  // cube in a corner it had never occupied.
  //
  // SWAP these, never append: setting top/right AND bottom/left
  // over-constrains the element, and which pair wins then depends on the
  // box model rather than on what was meant.
  this.viewHelperRenderer.domElement.style.position = 'absolute';
  this.viewHelperRenderer.domElement.style.bottom = '30px';
  this.viewHelperRenderer.domElement.style.left = '30px';
  this.viewHelperRenderer.domElement.style.zIndex = '1000';
  this.viewHelperRenderer.domElement.style.pointerEvents = 'none'; // Allow clicks to pass through
  this.container.appendChild(this.viewHelperRenderer.domElement);

    this.scene = new Scene()
    this.scene.autoUpdate = true
    window.scene = this.scene
    
    this.dimensionClass = new Dimensions(
      this.scene,
      this.container,
      this.camera,
      this.ReactApi.project
    )

    this.scene.background = new Color().setHSL(0.6, 0, 1)
    this.scene.fog = new Fog(this.scene.background, 1, 5000)

    this.scene.add(this.camera)

    // preserveDrawingBuffer OFF. With it on, the browser copies the whole
    // backbuffer every frame instead of swapping it - a cost that grows
    // with the canvas, paid sixty times a second for a screenshot wanted
    // a few times a session. Every reader of this canvas renders a frame
    // and reads it in the same task (captureFrame below; the issue pages
    // and the tracking stage do the same), which needs no preserved
    // buffer. Setting the flag on the renderer later is a no-op: it is a
    // context-creation attribute.
    // A handle to THIS viewer, whoever loaded the model. window.__sl is
    // the STREAMING loader, so it is null on a page that never streamed,
    // and it is nulled on dispose - which is why the owner pasting a
    // diagnostic got nothing back on 2026-09-20. The viewer itself is
    // the thing worth reaching, so it says where it is.
    if (typeof window !== 'undefined') window.__viewer = this;

    this.renderer = new WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: false,
      // ASK FOR THE REAL GPU. Without this the browser is free to give a
      // switchable-graphics laptop its INTEGRATED adapter, and on a
      // 300k-instance model that is the difference between a viewer and
      // a slideshow - with nothing on screen or in any log to say which
      // adapter you got. It was never set here, so every session since
      // the viewer existed has taken whatever the browser chose.
      // (owner-authorised 2026-09-23, while chasing "the models became
      // very very heavy".)
      powerPreference: 'high-performance'
    })
    // three >= 0.152 turns colour management on (sRGB output, linear
    // working space) and >= 0.155 defaults to physically-correct light
    // intensities; both change how every material and light in this
    // viewer looks. Keep the 0.135 appearance the exports were tuned
    // against: linear output, no colour conversion, legacy lights.
    try {
      ColorManagement.enabled = false;
      if ('outputColorSpace' in this.renderer) this.renderer.outputColorSpace = LinearSRGBColorSpace;
      if ('useLegacyLights' in this.renderer) this.renderer.useLegacyLights = true;
    } catch (e) { /* older three: nothing to do */ }
    this.renderer.setPixelRatio(1)
    this.renderer.setSize(this.container.offsetWidth, this.container.offsetHeight)

    this.container.appendChild(this.renderer.domElement)

    // Extract GPU info once at startup so a debug overlay can show
    // whether we got hardware accel. Detection heuristic: known
    // software renderer strings vs anything else (which is treated
    // as hardware). `WEBGL_debug_renderer_info` gives the unmasked
    // renderer string; Chromium hides it without the extension.
    try {
      const gl = this.renderer.getContext();
      const dbg = gl.getExtension('WEBGL_debug_renderer_info');
      const rawRenderer = dbg
        ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)
        : gl.getParameter(gl.RENDERER);
      const rawVendor = dbg
        ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL)
        : gl.getParameter(gl.VENDOR);
      const isSoftware = /SwiftShader|Software|Microsoft Basic Render|llvmpipe/i.test(rawRenderer || '');
      this._gpuInfo = {
        renderer: rawRenderer || 'unknown',
        vendor:   rawVendor   || 'unknown',
        hardwareAccelerated: !isSoftware,
      };
    } catch (e) {
      this._gpuInfo = { renderer: 'error', vendor: 'error', hardwareAccelerated: false };
    }

    this.setLights(this.scene)

    this.initControls()

    this._onResize = this.onWindowResize.bind(this)
    window.addEventListener('resize', this._onResize, {
      useCapture: true,
      passive: true
    })

    // The window is not the only thing that resizes the viewer: a
    // resizable split, a collapsing rail, the issue page switching
    // surfaces, or a pane going fullscreen all change the container
    // while the window stays exactly the same size. Without this the
    // canvas keeps its old dimensions and its old camera aspect, so the
    // picture stretches and the part of it you are pointing at is no
    // longer the part the projection thinks you are pointing at.
    if (typeof ResizeObserver !== 'undefined' && this.container) {
      this._resizeObserver = new ResizeObserver(() => {
        try { this.onWindowResize() } catch (e) { /* never break the frame loop */ }
      })
      this._resizeObserver.observe(this.container)
    }

    this.postProcessing = new PostProcessing(this.scene, this.camera, this.renderer)
    this.composer = this.postProcessing.composer

    setTimeout(() => {
      this.onWindowResize()
    }, 0)

    this.clipper = new Clipper(
      this.scene,
      this.loadedModels,
      this.renderer.domElement,
      this.camera,
      this.ReactApi.project,
      this.cameraControls,
      this.OrbitControls
    )

    this.animate(0);
  }

  start() {
    this.startTime = new Date()
  }

  end() {
    this.endTime = new Date()
    var timeDiff = this.endTime - this.startTime
    timeDiff /= 1000
    var seconds = Math.round(timeDiff)
  }

  // OnFitToScreen — frame the camera around a list of meshes/nodes,
  // optionally with extra raw bboxes (used by streaming fallback when
  // a uid's geometry isn't currently materialized).
  //
  // The extraBoxes parameter is a list of Box3 objects, typically the
  // bboxes of tiles that own selected uids whose geometry is unloaded.
  // We union them into the same target box so the camera flies toward
  // the right area of the model — geometry then materializes mid-flight
  // as the tile enters the frustum.
  OnFitToScreen(meshesOrNodes, extraBoxes = null) {
    this.scene.updateMatrixWorld(true);

    const box3 = new Box3();

    this.start();

    if (Array.isArray(meshesOrNodes) && meshesOrNodes.length) {
      for (const n of meshesOrNodes) {
        if (!n) continue;
        this.ExpandMesh(n, box3);
      }
    }

    // Streaming fallback: include tile bboxes for selected uids whose
    // geometry isn't currently materialized. Caller (targetItems) fills
    // this in when a uid is in totalSelection but absent from
    // meshesObject (tile unloaded, isolated out, etc.).
    if (Array.isArray(extraBoxes) && extraBoxes.length) {
      for (const b of extraBoxes) {
        if (b && !b.isEmpty()) box3.union(b);
      }
    }

    this.end();
    this.start();

    if (!box3.isEmpty()) {
      this.cameraControls.fitToBox(box3, true);
    } else {
      const roots = this.loadedModels?.length ? this.loadedModels : this.scene.children;
      const sceneBox = new Box3();
      for (const r of roots) {
        this.ExpandMesh(r, sceneBox);
      }
      if (!sceneBox.isEmpty()) {
        this.cameraControls.fitToBox(sceneBox, true);
      } else {
        this.cameraControls.fitToBox(this.scene, true);
      }
    }

    this.end();
  }

  ExpandMesh(meshObj, box3 = new Box3()) {
    const obj = meshObj?.mesh ? meshObj.mesh : meshObj;
    if (!obj) return box3;

    if (obj.isInstancedMesh || obj.isBatchedMesh || obj.isSlotMesh) {
      // PER-FRAGMENT SCOPE: when the caller passed a record (an entry
      // from meshesObject — has .fragments[] with explicit slot indices),
      // use THOSE indices directly. This is the correct per-element
      // framing: the bbox covers only the slots the user asked to frame.
      //
      // Falling back to totalSelection-filtering (the old behavior) was
      // the bug — when totalSelection didn't include the uid (e.g.,
      // tree-click before selection state propagates), the filter
      // matched nothing → null → _expandWorldBoxByObject iterated every
      // instance in the pool → bbox spanned every uid sharing that hash.
      let only = null;
      if (meshObj && Array.isArray(meshObj.fragments) && meshObj.fragments.length) {
        only = [];
        for (const f of meshObj.fragments) {
          if (f && f.mesh === obj && typeof f.index === 'number') {
            only.push(f.index);
          }
        }
        if (only.length === 0) only = null;
      } else if (meshObj && typeof meshObj.index === 'number') {
        only = [meshObj.index];
      }
      // Final fallback: if no record-level scope provided AND the
      // caller passed a bare mesh, defer to totalSelection-filtering.
      if (only === null) {
        const selectedIds = this._buildSelectedIdSet();
        only = this._getSelectedInstanceIndices(obj, selectedIds);
      }
      this._expandWorldBoxByObject(obj, box3, only);
      return box3;
    }

    this._expandWorldBoxByObject(obj, box3, null);
    return box3;
  }

  targetItem(uniqueId) {
    if (uniqueId == null) return;

    let entry = this.meshesObject.get(uniqueId);
    if (!entry && typeof uniqueId === 'string') {
      // Slow-scan: uid lives in another record's pool slot. Build a
      // synthetic per-uid record so OnFitToScreen → ExpandMesh scopes
      // to THIS uid's slot index, not the unrelated owner's fragments.
      for (const [, v] of this.meshesObject.entries()) {
        const m = v?.mesh;
        if (m?.isInstancedMesh && m.userData) {
          const data = m.userData;
          const len = (typeof data.length === 'number') ? data.length : 0;
          for (let i = 0; i < len; i++) {
            const ud = data[i];
            if (ud && ud.uniqueId === uniqueId) {
              entry = {
                mesh: m,
                uniqueId,
                index: i,
                fragments: [{ mesh: m, index: i }],
              };
              break;
            }
          }
        }
        if (entry) break;
      }
    }

    if (entry) {
      this.OnFitToScreen([entry]);
    } else {
      // Streaming fallback: uid isn't currently rendered. Fall back to
      // the host tile's bbox if the streaming pipeline knows about it.
      const tileBox = this._findTileBoxForUid(uniqueId);
      if (tileBox) {
        this.OnFitToScreen([], [tileBox]);
      } else {
        this.cameraControls.fitToBox(this.scene, true);
      }
    }
  }

  // targetItems — frame around all currently-selected uids.
  //
  // Streaming-aware: for each selected uid, first try meshesObject
  // (covers loaded + actively-rendered uids). If absent, scan
  // meshesObject's userData for the uid (covers cases where a uid is
  // packed into a different record's instanced mesh). If still absent,
  // fall back to the host tile's bbox via the streaming registry —
  // this covers uids whose tiles have unloaded due to frustum culling
  // OR uids that are isolated/hidden but should still be framable.
  // F, and "frame what I selected".
  //
  // WHY IT LOOKED RANDOM TO THE OWNER: the slow scan below is guarded by
  // `m?.isInstancedMesh`, so it only ever looked inside POOLED meshes. An
  // element packed into a tile BATCH or a MERGED tile mesh was never found,
  // fell through to its host TILE's bbox, and F framed the whole tile or
  // nothing. Pooled worked, batched and merged did not - and nothing
  // visible about an element tells you which it is, so "F does not work on
  // some fragments" was exactly true and impossible to predict.
  //
  // The adapter knows all three. unionBoxForUids walks slotsByUid, which
  // indexes pools, batches and merged tiles alike, so the blind spot goes
  // away by construction rather than by adding a third branch to a scan.
  // A RULE-shaped selection (a whole category) has no uid list at all and
  // is framed through unionBoxForRule.
  //
  // `reason` is returned so "F did nothing" stops being silent: a caller
  // can say "nothing to frame - those elements have not loaded yet"
  // instead of leaving a person unable to tell a no-op from a bug.
  targetItems({ rule = null } = {}) {
    const sl = (typeof window !== 'undefined') ? window.__sl : null;
    const adapter = sl?.streamingController?.adapter;
    // THE SAME UNION HIDE USES, for the same reason. Framing read
    // `totalSelection` only, so on the tracking page - which keeps its
    // selection in React and paints it with highlightUids, never
    // selectUids - F had nothing to frame no matter how much was lit, and
    // the owner reported the default view buttons "not working ... like
    // framing" (2026-09-28). One definition of "the selection", shared
    // with hideSelected, so the two cannot drift again.
    const picked = selectionForHide(this.totalSelection, pageSelectionOf(this.ReactApi));
    if (adapter && (rule || picked.length)) {
      // The FAMILY's slots: a nested element has none of its own, so its
      // box is the union of its parts' (./nestedParts).
      const uids = rule ? null : expandToFamily(picked);
      // The controller can say where a HELD row would be. Without this the
      // box ignores every deferred element, so F does nothing for exactly
      // the elements that get deferred - small parts seen from far away,
      // which is the case the owner reported.
      const _ctrl = (typeof window !== "undefined" && window.__sl) ? window.__sl.streamingController : null;
      const _deferred = _ctrl && typeof _ctrl.deferredPlacementsFor === "function"
        ? (list) => _ctrl.deferredPlacementsFor(list)
        : null;
      const plan = planFraming(adapter, uids, this.camera, { mode: 'always', rule, deferred: _deferred });
      if (plan.shouldFrame && !plan.box.isEmpty()) {
        this.scene.updateMatrixWorld(true);
        this.cameraControls.fitToBox(plan.box, true);
        this.requestRender(0);
        return null;
      }
      // Nothing resolved: every selected element is in a tile that has not
      // streamed, or is hidden. Fall through to the older path, which can
      // still point at the right AREA using tile boxes, and report why.
      if (plan.reason === 'empty' && !picked.length) return 'empty';
    }

    const meshes = [];
    const tileBoxes = [];

    for (const sel of picked) {
      const hit = this.meshesObject.get(sel);
      if (hit) {
        meshes.push(hit);
        continue;
      }

      // Slow scan: uid may live in a slot owned by a different
      // record's InstancedMesh (pool architecture — multiple uids per
      // pool). When found, push a synthetic per-uid record so
      // ExpandMesh can scope to THIS uid's slot index, not the
      // unrelated owner record's fragments.
      let found = false;
      for (const [, v] of this.meshesObject.entries()) {
        const m = v?.mesh;
        if (m?.isInstancedMesh && m.userData) {
          const data = m.userData;
          const len = (typeof data.length === 'number') ? data.length : 0;
          for (let i = 0; i < len; i++) {
            const ud = data[i];
            if (ud && ud.uniqueId === sel) {
              meshes.push({
                mesh: m,
                uniqueId: sel,
                index: i,
                fragments: [{ mesh: m, index: i }],
              });
              found = true;
              break;
            }
          }
          if (found) break;
        }
      }

      if (found) continue;

      // Streaming fallback: uid isn't rendered anywhere. Try to find
      // its host tile's bbox in the streaming registry. Points camera
      // at the right area; geometry materializes once tile enters
      // frustum.
      const tileBox = this._findTileBoxForUid(sel);
      if (tileBox) tileBoxes.push(tileBox);
    }

    if (!meshes.length && !tileBoxes.length) return 'empty';
    this.OnFitToScreen(meshes, tileBoxes);
    return null;
  }

  // HOME: the whole building, from the angle the Home button always
  // gave. ONE definition, used by the button AND by the viewer on load
  // (frameModelOnce), so the two can never drift - the owner asked for
  // exactly that: "the camera should get positioned on the Home view, to
  // show the full building", rather than landing somewhere arbitrary.
  //
  // It used to fly to a HARDCODED (0, 130, 250) looking at (0,0,0),
  // which frames nothing: it only looked right for a model that happens
  // to sit near the origin, and was quietly wrong for every model that
  // does not. The angle below is that same view expressed as angles -
  // front, a little above the horizon - and the DISTANCE now comes from
  // the model's own bounds.
  static get HOME_ANGLES() {
    // azimuth 0 = front; polar 1.09 rad ~ 27 degrees above the horizon,
    // which is what (0, 130, 250) was.
    return [0, 1.09];
  }

  onInitialPosition({ transition = true } = {}) {
    if (!this.cameraControls) return;
    this.cameraControls.enabled = true;
    if (this.OrbitControls) this.OrbitControls.enabled = false;
    const [azimuth, polar] = Viewer.HOME_ANGLES;
    this.cameraControls.rotateTo(azimuth, polar, transition);
    this.fitToModel(transition);
    this.requestRender(0);
  }

  // A picture of what is on screen right now. Render, then read, with
  // nothing in between: the canvas keeps its pixels only until the next
  // frame is composited, so a read that is not preceded by a draw in the
  // same task gets a blank. Goes through the composer when there is an
  // outline to draw, straight to the canvas otherwise.
  // ONE PLACE THAT DECIDES HOW A FRAME IS DRAWN.
  //
  // There used to be eight, each choosing for itself between the depth
  // composite, the composer and a bare renderer.render - the main loop, the
  // capture, two loaders, the legacy fallback, and five call sites on the
  // tracking stage. That is why the owner kept finding new ways the effects
  // "do not hold": he was walking into those decisions one at a time. Every
  // caller now asks this, so there is no ninth way to get it wrong.
  //
  //   moving   the camera moved since the last frame, so the composite may
  //            hold its prepass. A capture is a single cold frame and must
  //            say false, or it would composite against a stale prepass.
  //   busy     a model is loading, so the streaming shortcut is allowed.
  //   allowDirect
  //            a direct render is an acceptable picture when there is
  //            nothing to outline. The main loop allows it only while
  //            BUSY - the composer is otherwise the same picture for one
  //            pass instead of three. A CAPTURE allows it always, because
  //            it is one frame and the composer's extra full-screen copy
  //            buys nothing when there is no outline to draw.
  //   allowComposite
  //            whether the depth composite may draw this frame at all.
  // Returns which path drew, for tests and for perfSnapshot.
  drawFrame({ delta = 0, moving = false, busy = false, allowDirect = false, allowComposite = true } = {}) {
    if (!this.renderer || !this.scene || !this.camera) return 'none';
    const pp = this.postProcessing;
    const compositeOn = !!(allowComposite && this._composite && compositeState.enabled
      && pp && pp.compositePass && pp.compositePass.enabled);
    if (compositeOn) {
      pp.compositePass.moving = moving;
      pp.composer.render(delta);
      return 'composite';
    }
    // The composite is a pass, so switching it off for THIS frame means
    // taking it out of the chain for this frame - otherwise the pass would
    // draw it anyway and allowComposite would be a lie.
    const restore = (pp && pp.compositePass && pp.compositePass.enabled && !allowComposite)
      ? (pp.compositePass.enabled = false, pp.renderPass.enabled = true, true) : false;
    try {
      if ((busy || allowDirect) && Viewer.canSkipComposer(pp, this._hoverOutline)) {
        this.renderer.render(this.scene, this.camera);
        return 'direct';
      }
      if (pp && pp.composer) {
        pp.composer.render(delta);
        return 'composer';
      }
      this.renderer.render(this.scene, this.camera);
      return 'direct';
    } finally {
      if (restore) { pp.compositePass.enabled = true; pp.renderPass.enabled = false; }
    }
  }

  // Whether a CAPTURE may run the depth composite.
  //
  // The owner ruled that screenshots should match what he sees: a picture
  // that does not match the screen is a small lie in an evidence record,
  // and nobody would ever report it because a still gives you nothing to
  // compare against.
  //
  // MEASURED before switching on, because a capture is a single COLD frame
  // and pays the full prepass with none of the relief the screen gets from
  // freezing it while the camera moves: 599 ms plain against 1348 ms
  // composited - 2.25x, +749 ms - at 40,000 instances and 1400x900 under
  // the software GL this box renders headless work on. Next to a person
  // waiting for a page that is nothing.
  //
  // What this gates is captureFrame: issue evidence, the tracking stage's
  // export picture, and thumbnails read off a live canvas. It does NOT
  // gate the technical view PDF - that is a DOM/SVG print of
  // /documentation/:id?print=1 through page.pdf(), with no viewer, no
  // three and no canvas anywhere in it.
  //
  // The one thing still worth watching is a thumbnail SWEEP: one capture
  // is free, a batch of hundreds is 2.25x a job that already exists.
  static get CAPTURE_COMPOSITE() { return true; }

  captureFrame(mime = 'image/png', quality) {
    if (!this.renderer || !this.scene || !this.camera) return null
    try {
      this.drawFrame({ moving: false, allowDirect: true, allowComposite: Viewer.CAPTURE_COMPOSITE });
      return this.renderer.domElement.toDataURL(mime, quality)
    } catch (e) {
      return null
    }
  }

  // The anchor is not the page's job - see webgl/js/viewer/saveDataUrl.js for
  // why this threw on seven of the nine pages that show the camera button.
  onScreenShot() {
    const imgData = this.captureFrame('image/jpeg')
    if (!imgData) return
    saveDataUrl(imgData, 'bidlight_screenshot')
  }

  initControls() {
    CameraControls.install({ THREE: subsetOfTHREE });
    this.cameraControls = new CameraControls(this.camera, this.renderer.domElement);
    this.cameraControls.enabled = true;

    this.cameraControls.mouseButtons.left = CameraControls.ACTION.ROTATE;
    this.cameraControls.mouseButtons.right = CameraControls.ACTION.TRUCK;
    this.cameraControls.mouseButtons.middle = CameraControls.ACTION.DOLLY;
    this.cameraControls.mouseButtons.wheel = CameraControls.ACTION.DOLLY;
    
    this.cameraControls.touches.one = CameraControls.ACTION.TOUCH_ROTATE;
    this.cameraControls.touches.two = CameraControls.ACTION.TOUCH_DOLLY_TRUCK;
    this.cameraControls.touches.three = CameraControls.ACTION.TOUCH_TRUCK;

    this.cameraControls.minDistance = 2;
    this.cameraControls.maxDistance = 1000;
    this.cameraControls.dampingFactor = 0.05;
    this.cameraControls.draggingDampingFactor = 0.25;
    this.cameraControls.dollyToCursor = true;
    this.cameraControls.infinityDolly = true;
    this.cameraControls.dollySpeed = 0.6;
    this.cameraControls.truckSpeed = 2.0;
    
    this.cameraControls.maxPolarAngle = Math.PI;
    this.cameraControls.minPolarAngle = 0;

    this.OrbitControls = new OrbitControls(this.camera, this.renderer.domElement);
    this.OrbitControls.target = new Vector3(0, 0, 0);
    this.OrbitControls.enabled = false;

    this.cameraControls.addEventListener("controlstart", () => {
      if (this.autoSpin) this.stopAutoSpin();
    });

    // A loader that published its bounds before the controls existed.
    this._applyDollyRange(this.modelBounds);
  }
// Add this method after the setLights method:

updateLightingForModel(modelInfo) {
  const { size, center, bounds } = modelInfo;
  
  console.log('Updating lighting for model:', modelInfo);
  
  // Calculate appropriate distances
  const maxDimension = Math.max(size.x, size.y, size.z);
  const skyRadius = maxDimension * 10;
  
  // Update fog to match model size
  if (this.scene.fog) {
    this.scene.fog.near = maxDimension * 0.1;
    this.scene.fog.far = skyRadius * 0.8;
  }

  // Re-derive the dolly range and the fog's reach together: this runs
  // after the bounds were published, and it has just overwritten the
  // fog.
  this._applyDollyRange(bounds);
  
  // Remove old sky dome if it exists
  const oldSky = this.scene.getObjectByName('skyDome');
  if (oldSky) {
    this.scene.remove(oldSky);
    if (oldSky.geometry) oldSky.geometry.dispose();
    if (oldSky.material) oldSky.material.dispose();
  }
  
  // Create new appropriately-sized sky dome
  const skyGeo = new SphereGeometry(skyRadius, 32, 15);
  
  const uniforms = {
    topColor: { value: new Color(0x0077ff) },
    bottomColor: { value: new Color(0xffffff) },
    offset: { value: 33 },
    exponent: { value: 0.6 }
  };
  
  const skyMat = new ShaderMaterial({
    uniforms: uniforms,
    vertexShader: this.vertexShader,
    fragmentShader: this.fragmentShader,
    side: BackSide
  });

  const sky = new Mesh(skyGeo, skyMat);
  sky.name = 'skyDome';
  sky.position.copy(center); // Position at model center (should be 0,0,0 after centering)
  
  this.scene.add(sky);
  
  console.log('Sky dome updated:', {
    radius: skyRadius,
    center: center,
    modelSize: size,
    fogNear: this.scene.fog.near,
    fogFar: this.scene.fog.far
  });
}
setLights = (scene) => {
  scene.background = new Color().setHSL(0.6, 0, 1)
  scene.fog = new Fog(scene.background, 1, 5000)

  // LIGHTS
  const hemiLight = new HemisphereLight(0xffffff, 0xffffff, 0.6)
  hemiLight.color.setHSL(0.6, 1, 0.6)
  hemiLight.groundColor.setHSL(0.095, 1, 0.75)
  hemiLight.position.set(0, 50, 0)
  scene.add(hemiLight)

  // No HemisphereLightHelper here, and no DirectionalLightHelper below.
  // Both are three's DEBUGGING aids - they draw a marker where a light
  // is - and both were added to every customer scene unconditionally.
  // The directional one is the "sun shape" the owner saw in every
  // project (2026-09-20). A helper only DRAWS; the lights themselves are
  // added above and below and are untouched, so nothing about the
  // lighting changes.

  const dirLight = new DirectionalLight(0xffffff, 1)
  dirLight.color.setHSL(0.1, 1, 0.95)
  dirLight.position.set(-1, 1.75, 1)
  dirLight.position.multiplyScalar(60)

  dirLight.castShadow = true
  dirLight.shadow.mapSize.width = 2048
  dirLight.shadow.mapSize.height = 2048

  const d = 50
  dirLight.shadow.camera.left = -d
  dirLight.shadow.camera.right = d
  dirLight.shadow.camera.top = d
  dirLight.shadow.camera.bottom = -d
  dirLight.shadow.camera.far = 3500
  dirLight.shadow.bias = -0.0001
  
  scene.add(dirLight)

  const ambientlight = new AmbientLight(0xffffff, 0.2)
  scene.add(ambientlight)

  // SKYDOME - Create a temporary one, will be replaced when model loads
  const vertexShader = this.vertexShader
  const fragmentShader = this.fragmentShader
  const uniforms = {
    topColor: { value: new Color(0x0077ff) },
    bottomColor: { value: new Color(0xffffff) },
    offset: { value: 33 },
    exponent: { value: 0.6 }
  }
  uniforms['topColor'].value.copy(hemiLight.color)

  scene.fog.color.copy(uniforms['bottomColor'].value)

  // Start with a reasonable default size
  const skyGeo = new SphereGeometry(1000, 32, 15)
  const skyMat = new ShaderMaterial({
    uniforms: uniforms,
    vertexShader: vertexShader,
    fragmentShader: fragmentShader,
    side: BackSide
  })

  const sky = new Mesh(skyGeo, skyMat)
  sky.name = 'skyDome'
  scene.add(sky)

  return scene
}

  // Release what the viewer holds onto. The issue page unmounts and
  // remounts this canvas every time someone switches surface, and the
  // ResizeObserver's callback keeps the whole viewer (scene, renderer,
  // GPU resources) reachable until it is disconnected.
  destroy() {
    try {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect()
        this._resizeObserver = null
      }
    } catch (e) { /* teardown must not throw */ }
    // The render loop is a self-scheduling requestAnimationFrame chain;
    // this flag is what stops it. Disposing the renderer is left to the
    // caller, which already does it - and must happen AFTER the loop has
    // stopped, not while it is still drawing.
    this._destroyed = true;
    try {
      if (this._onResize) window.removeEventListener('resize', this._onResize)
      if (this._onKeyDown) window.removeEventListener('keydown', this._onKeyDown)
    } catch (e) { /* teardown must not throw */ }
    try { if (this._rodUnlisten) this._rodUnlisten(); } catch (e) { /* teardown must not throw */ }
    this._rodUnlisten = null;
    // The composite holds three full-screen targets (~17 MB at
    // 1440x900). A viewer torn down without giving them back would leak
    // that much per page visit, which is exactly the kind of growth the
    // effects were supposed to avoid.
    try { if (this._composite) this._composite.dispose(); } catch (e) { /* teardown must not throw */ }
    this._composite = null;
    try { if (this._gridLabelOverlay) this._gridLabelOverlay.dispose(); } catch (e) { /* teardown must not throw */ }
    this._gridLabelOverlay = null;
    // And let go of the handle, so a stale viewer is never what a
    // diagnostic reads.
    try { if (typeof window !== 'undefined' && window.__viewer === this) window.__viewer = null; } catch (e) { /* best effort */ }
  }

  onWindowResize() {
    // clientWidth/Height, not offsetWidth/Height: the offset pair
    // includes the container's border, and renderer.setSize writes that
    // number to the canvas's CSS size - so a bordered container grew a
    // canvas slightly larger than the space it had, which then sat
    // offset inside it.
    const w = this.container.clientWidth || this.container.offsetWidth
    const h = this.container.clientHeight || this.container.offsetHeight
    if (!w || !h) return
    this.camera.aspect = w / h
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(w, h)

    this.dimensionClass.renderer2D.setSize(w, h)

    this.postProcessing.composer.setSize(w, h)
  }
 onDocumentMouseWheel(event) {
    var fovMAX = 160
    var fovMIN = 1

    this.camera.fov -= event.wheelDeltaY * 0.03
    this.camera.fov = Math.max(Math.min(this.camera.fov, fovMAX), fovMIN)
    this.camera.projectionMatrix = new Matrix4().makePerspective(
      this.camera.fov,
      window.innerWidth / window.innerHeight,
      this.camera.near,
      this.camera.far
    )
  }

  onZoom(n) {
    this.camera.layers.toggle(0)
    let zoom = (this.camera.zoom += 10 * n)

    this.cameraControls.zoomTo(zoom, false)
    this.camera.updateProjectionMatrix()
    this.camera.layers.enableAll()
  }

  // Named views, as azimuth and polar angles.
  //
  // Polar is measured from straight up, so 0 looks down at the plan and
  // half a turn looks up from underneath. Neither is used exactly:
  // camera-controls degenerates at the poles, and a hair off reads the
  // same on screen.
  // How often to render while streaming, from what the last frames
  // cost. Rendering gets at most about a third of wall time: the rest
  // is for fetch dispatch, parsed-buffer hand-off, GPU upload and the
  // materialisation slice, which all share the main thread with it.
  // Never faster than the floor (15 fps while tiles materialise, 30
  // while only fetches are pending) and never slower than 2 fps, so the
  // picture keeps moving on the heaviest canvas.
  static busyRenderInterval(renderSamples, loading) {
    const floor = loading ? 66 : 33;
    const arr = Array.isArray(renderSamples) ? renderSamples.slice(-20).filter((v) => Number.isFinite(v) && v >= 0) : [];
    if (!arr.length) return floor;
    const sorted = arr.slice().sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    return Math.max(floor, Math.min(500, Math.round(median * 3)));
  }

  // The composer is only needed when it has something to add: an
  // outline on a hovered or selected object. Otherwise a direct render
  // is the same picture for one pass instead of three.
  static canSkipComposer(postProcessing, hoverOutline) {
    if (hoverOutline) return false;
    const pass = postProcessing && postProcessing.outlinePass;
    if (!pass) return true;
    return !(Array.isArray(pass.selectedObjects) && pass.selectedObjects.length > 0);
  }

  static get STANDARD_VIEWS() {
    const P = Math.PI;
    return {
      top: [0, 0.0001],
      bottom: [0, P - 0.0001],
      front: [0, P / 2],
      back: [P, P / 2],
      left: [-P / 2, P / 2],
      right: [P / 2, P / 2],
      iso: [P / 4, P / 3],
    };
  }

  // Frame the whole model from where the camera already is.
  // Put the camera on the model, ONCE, when nobody has placed it.
  //
  // The camera is constructed at (0,0,0) and camera-controls' target
  // defaults to (0,0,0) - the same point. Nothing framed the model on
  // load (no loader calls fitToModel or onInitialPosition; Home is a
  // button), so the viewer opened with camera and target coincident,
  // and that is degenerate: rotating about a target at zero distance
  // moves nothing on screen. The owner reported it as "the mouse is
  // ignored until I scroll" - a wheel dolly is what finally separates
  // the two and makes rotation work - and as "it does not frame the
  // building on load" (2026-09-20). One cause, both symptoms.
  //
  // Only when the camera is still at its constructed origin, so a page
  // that places its own camera (an issue's saved viewpoint, a tracking
  // view) is never overridden.
  frameModelOnce() {
    if (this.__framedOnce || !this.cameraControls || !this.camera) return false;
    const p = this.camera.position;
    if (p.x !== 0 || p.y !== 0 || p.z !== 0) { this.__framedOnce = true; return false; }
    const box = this._modelBoundsForGrid();
    if (!box || box.isEmpty()) return false;      // no bounds yet: try again when they arrive
    this.__framedOnce = true;
    // EXACTLY what the Home button does, without the flight - so the
    // opening view and Home can never differ.
    this.onInitialPosition({ transition: false });
    return true;
  }

  fitToModel(transition = true) {
    if (!this.cameraControls) return;
    const box = this._modelBoundsForGrid();
    if (box && !box.isEmpty()) this.cameraControls.fitToBox(box, transition);
    else this.cameraControls.fitToBox(this.scene, transition);
  }

  // Frame a SET of elements (the tracking tree's ticks, a multi-select).
  //
  //   mode 'auto'   (default) move only when the selection is not already
  //                 comfortably visible - so ticking something in front of
  //                 you does NOT pull the camera away from your work;
  //   mode 'always' re-frame on every change (the person's toggle);
  //   mode 'never'  leave the camera alone.
  //
  // Returns the reason it moved (or null), so a caller can say why - and
  // so "it randomly moves the camera" is answerable rather than arguable.
  // Callers debounce: ONE call per SETTLED selection, never one per tick.
  frameSelectionUids(uids, { mode = 'auto', transition = true, rule = null } = {}) {
    if (!this.cameraControls || (!uids && !rule)) return null;
    const sl = (typeof window !== 'undefined') ? window.__sl : null;
    const adapter = sl?.streamingController?.adapter;
    if (!adapter) return null;

    this.scene.updateMatrixWorld(true);
      // The controller can say where a HELD row would be. Without this the
      // box ignores every deferred element, so F does nothing for exactly
      // the elements that get deferred - small parts seen from far away,
      // which is the case the owner reported.
      const _ctrl = (typeof window !== "undefined" && window.__sl) ? window.__sl.streamingController : null;
      const _deferred = _ctrl && typeof _ctrl.deferredPlacementsFor === "function"
        ? (list) => _ctrl.deferredPlacementsFor(list)
        : null;
    // A nested element frames by its parts - it has no slot of its own.
    const frameUids = uids ? expandToFamily(uids) : uids;
    const plan = planFraming(adapter, frameUids, this.camera, { mode, rule, deferred: _deferred });
    if (!plan.shouldFrame || plan.box.isEmpty()) return plan.reason;
    this.cameraControls.fitToBox(plan.box, transition);
    this.requestRender(0);
    return plan.reason;
  }

  // Turn to a named view and frame the model from it. Rotating first and
  // fitting second is what makes "top" mean the plan of the whole model
  // rather than the plan of wherever the camera happened to be.
  setStandardView(name) {
    if (!this.cameraControls) return;
    const angles = Viewer.STANDARD_VIEWS[name];
    if (!angles) return;
    this.cameraControls.rotateTo(angles[0], angles[1], true);
    this.fitToModel();
  }

  // How far out the wheel may go, and how close it may come.
  //
  // Both were constants: 2 and 1000. A thousand units is a room, and the
  // sites this opens are several thousand across, so zooming out stopped
  // with the model still overflowing the screen. With infinityDolly on,
  // stopping does not feel like a limit - past the maximum the TARGET
  // slides instead of the camera receding, so the picture stops changing
  // while the wheel keeps turning. That is the "zoom gets stuck".
  //
  // It also clamped the automatic framing, which asks the same controls
  // to pull back far enough to hold the bounding box: a large model
  // could not be framed at all, only approached.
  //
  // So the range is sized from the model instead. Eight times its
  // longest side is well past the distance that frames it, and the near
  // limit scales down for a small model without letting a large one push
  // it out to a distance that would refuse to inspect anything.
  _applyDollyRange(box) {
    if (!this.cameraControls || !box || box.isEmpty()) return;
    const size = new Vector3();
    box.getSize(size);
    const span = Math.max(size.x, size.y, size.z);
    if (!Number.isFinite(span) || span <= 0) return;
    this.cameraControls.maxDistance = Math.max(1000, span * 8);
    // THE NEAR LIMIT DECIDES WHETHER ZOOM EVER ESCAPES ITS SLOW ZONE.
    //
    // Owner, 2026-09-27: "zoom stopped speeding after many zooms when the
    // zoom starts moving very slowly; it used to pick up speed after 3 or 4
    // low-distance zooms."
    //
    // With infinityDolly on, camera-controls slides the TARGET once a dolly
    // would take the camera past minDistance - that slide IS the speed-up he
    // remembers. The dolly step is proportional to the current distance, so
    // the camera approaches the limit GEOMETRICALLY and only escapes when it
    // finally crosses it.
    //
    // f960ccdc - correctly fixing the FAR limit for large sites - also
    // replaced a flat near limit of 2 with span*0.001. On a small model that
    // is a near limit of about 0.12, and the crossing moves from roughly the
    // 9th zoom to the 16th. MEASURED on Project1, staging main.c0ad04fc:
    // minDistance 0.1227, fourteen dollies took the camera 137.96 -> 0.332
    // with the step shrinking 48.28 -> 0.18 and NEVER jumping.
    //
    // So the scaling stays - a genuinely tiny model should still be
    // inspectable - but the coefficient is raised so anything of room size or
    // larger keeps the old near limit of 2 and the old feel. span*0.02 gives
    // 2 for any span above 100 units, and scales below it.
    this.cameraControls.minDistance = Math.min(2, Math.max(0.05, span * 0.02));

    // Fog is measured from the camera and was scaled to the model, which
    // was safe only because the camera could never get more than a
    // thousand units away. Standing back far enough to frame a site now
    // puts that site at the fog's far plane, so it would dissolve into
    // the background at the exact distance it is finally whole. The fog
    // has to reach past wherever the camera may go.
    if (this.scene && this.scene.fog) {
      const reach = this.cameraControls.maxDistance + span;
      if (this.scene.fog.far < reach) this.scene.fog.far = reach;
    }
  }

  // Published by whichever loader is in play - the streaming one from the
  // manifest, the legacy one from its instanced meshes. Kept as an
  // accessor so the dolly range follows the model without either loader
  // having to know that it does.
  get modelBounds() { return this._modelBounds || null; }

  set modelBounds(box) {
    this._modelBounds = box;
    this._applyDollyRange(box);
  }

  // The model's bounds in viewer space: what the loaders publish from the
  // manifest (streaming) or the instanced meshes (legacy), else a walk of
  // what is in the scene right now. Null when nothing is known.
  _modelBoundsForGrid() {
    const published = this.modelBounds
    if (published && !published.isEmpty()) return published
    const roots = this.loadedModels?.length ? this.loadedModels : this.scene.children
    const box = new Box3()
    for (const r of roots) {
      if (r === this.scene.grid) continue
      this.ExpandMesh(r, box)
    }
    return box.isEmpty() ? null : box
  }

  // The exported building grid, handed in by the page once the document
  // set is known. Held as DATA, not geometry: the grid is not built until
  // somebody presses G, because most sessions never do.
  // `source` is root-04's grid-source answer, { docName, how, ... } or
  // null when it could not be reached. `how` is carried because the
  // route always returns its BEST candidate and its last resort is "the
  // document with the most lines" - a bet, not a finding, and the viewer
  // must not show the two with the same confidence.
  setBuildingGrid(byDocument, source = null) {
    this._buildingGridData = {
      byDocument: byDocument || [],
      preferredDoc: (source && source.docName) || null,
      how: (source && source.how) || null,
    }
    // A grid already on screen was built from the old data. Drop it so
    // the next toggle rebuilds; without this, data that arrives after a
    // first press is ignored for the rest of the session and the owner
    // keeps the fallback lattice with no way to get back.
    if (this.scene.grid) {
      const wasVisible = this.scene.grid.visible
      this._disposeGrid()
      this.onShowGrid()
      this._setGridVisible(wasVisible)
    } else if (this._gridWanted) {
      // Pressed G while the data was still loading. Honour the press now
      // rather than making them press again.
      this._gridWanted = false
      this.onShowGrid(document.getElementById('gridToggleBtn'))
    }
  }

  // The page registers a LOADER rather than data, and it is called once,
  // on the first G press. See ModelViewer.loadBuildingGrid: fetching at
  // model load cost every session one round-trip per document - all
  // seventeen of them on BA03, in series - for a control most sessions
  // never touch.
  setBuildingGridLoader(fn) {
    this._buildingGridLoader = typeof fn === 'function' ? fn : null
  }

  // Fetch the grid on demand. Returns true when a press should WAIT for
  // it rather than draw the fallback: showing the anonymous lattice for
  // a moment and swapping it for the real grid would flash a generic
  // grid as the building's, which is the thing this feature exists to
  // avoid.
  _ensureBuildingGrid() {
    if (this._buildingGridData || !this._buildingGridLoader || this._gridLoading) return false
    this._gridLoading = true
    const notify = this.ReactApi && typeof this.ReactApi.notify === 'function'
      ? (m) => this.ReactApi.notify(m)
      : null
    if (notify) notify('Loading the building grid...')
    Promise.resolve()
      .then(() => this._buildingGridLoader())
      .then((got) => {
        this._gridLoading = false
        if (got && got.byDocument && got.byDocument.length) {
          this.setBuildingGrid(got.byDocument, got.source)
          return
        }
        // No grid in this export. Record that we asked, so the next press
        // does not fetch again, and let the press fall through to the
        // reference grid.
        this._buildingGridData = { byDocument: [], preferredDoc: null, how: null }
        if (this._gridWanted) {
          this._gridWanted = false
          this.onShowGrid(document.getElementById('gridToggleBtn'))
        }
      })
      .catch(() => {
        this._gridLoading = false
        this._buildingGridData = { byDocument: [], preferredDoc: null, how: null }
        if (this._gridWanted) {
          this._gridWanted = false
          this.onShowGrid(document.getElementById('gridToggleBtn'))
        }
      })
    return true
  }

  _disposeGrid() {
    const grid = this.scene.grid
    if (!grid) return
    grid.traverse((o) => {
      if (o.geometry) o.geometry.dispose()
      if (o.material) o.material.dispose()
    })
    this.scene.remove(grid)
    this.scene.grid = null
    if (this._gridLabelOverlay) this._gridLabelOverlay.setLines([])
  }

  // Lines AND labels move together - the owner asked for one button that
  // shows and hides the grid "with their label". The labels follow
  // grid.visible on the next frame (gridLabelOverlay.draw), which the
  // requestRender below asks for.
  _setGridVisible(visible) {
    const grid = this.scene.grid
    if (!grid) return
    grid.visible = visible
    this.requestRender(0)
  }

  // The building's own grid, or null when this project has none.
  _buildBuildingGrid() {
    const data = this._buildingGridData
    if (!data || !data.byDocument || !data.byDocument.length) return null
    const picked = selectGridSource(data.byDocument, data.preferredDoc)
    // Why there is no building grid matters to the person looking at it:
    // "this export has none" and "this export has 17 and I cannot tell
    // which is the building's" call for different reactions, and the
    // second is temporary. Kept so the fallback can say which it is.
    this._gridFallbackReason = picked.reason
    this._gridCandidates = picked.candidates || 0
    if (!picked.lines.length) return null

    // Drawn at the model's floor, where the old helper sat: it is a plan
    // reference, and at any other height it reads as a ceiling.
    const bounds = this._modelBoundsForGrid()
    const elevation = bounds && isFinite(bounds.min.y) ? bounds.min.y : 0
    const draw = buildGridDraw(picked.lines, elevation)

    const group = new Object3D()
    group.name = 'buildingGrid'

    const geometry = new BufferGeometry()
    geometry.setAttribute('position', new Float32BufferAttribute(draw.positions, 3))
    const segments = new LineSegments(geometry, new LineBasicMaterial({
      color: 0x4a90d9,
      transparent: true,
      opacity: 0.85,
      // Read through the model, the way a reference grid behaves in
      // Navisworks - a grid buried inside the slab is not a reference.
      depthTest: false,
    }))
    segments.renderOrder = 2
    group.add(segments)

    // The labels are NOT in the scene: they are drawn on one 2D canvas
    // over the viewer (./gridLabelOverlay), clipped to the screen so they
    // follow their lines on zoom, and without the whole-scene walk that
    // CSS2DRenderer did every frame to place DOM labels.
    group.userData.labelLines = draw.labelsSuppressed ? [] : draw.labelLines

    group.userData.gridInfo = {
      kind: 'building',
      document: picked.name,
      reason: picked.reason,
      how: data.how,
      // A single-document project is certain by arithmetic, whatever the
      // route said: there is nothing else it could be.
      confidence: picked.reason === 'only-document' ? 'known' : gridConfidence(data.how),
      lines: draw.lineCount,
      labelsSuppressed: draw.labelsSuppressed,
    }
    return group
  }

  // The old anonymous lattice, kept ONLY as the fallback for a project
  // with no exported grid, and never called a building grid where the
  // owner can see it: an arbitrary 1000-unit lattice presented as the
  // building's grid lines is worse than showing no grid at all.
  _buildReferenceGrid() {
    // Place the grid AT THE MODEL, not at the origin.
    //
    // Exports used to be centred by the plugin's crop offset, so a grid
    // at (0, 0, 0) sat exactly under the building. Since 2026-09-04 the
    // plugin exports in Revit's own coordinates and applies no offset;
    // the camera fits itself to the manifest bounds and never noticed,
    // but a grid left at the origin ended up a site's worth of feet
    // away from the model — below it, beside it, or both. So: centre it
    // on the model's footprint, put it at the model's floor, and size
    // it to the footprint (never smaller than the old 1000 units).
    const bounds = this._modelBoundsForGrid()
    let size = 1000
    const center = new Vector3(0, 0, 0)
    if (bounds) {
      bounds.getCenter(center)
      center.y = bounds.min.y
      const extent = Math.max(bounds.max.x - bounds.min.x, bounds.max.z - bounds.min.z)
      if (isFinite(extent) && extent > 0) size = Math.max(1000, Math.ceil(extent * 1.5 / 100) * 100)
    }
    const divisions = Math.max(10, Math.round(size / 10))
    const gridHelper = new GridHelper(size, divisions)
    gridHelper.position.copy(center)
    gridHelper.name = 'groundGrid'
    gridHelper.userData.gridInfo = {
      kind: 'reference',
      document: null,
      reason: this._gridFallbackReason || 'no-grid',
      candidates: this._gridCandidates || 0,
      lines: 0,
    }
    return gridHelper
  }

  // Has the camera turned since the cube was last drawn? Allocation-free:
  // this is asked on every animation frame. Returns true the first time,
  // so the cube always gets its initial draw.
  _cubeMoved() {
    const q = this.camera && this.camera.quaternion;
    if (!q) return false;
    const p = this._cubeQ;
    if (!p) { this._cubeQ = new Float64Array([q.x, q.y, q.z, q.w]); return true; }
    if (p[0] === q.x && p[1] === q.y && p[2] === q.z && p[3] === q.w) return false;
    p[0] = q.x; p[1] = q.y; p[2] = q.z; p[3] = q.w;
    return true;
  }

  onShowGrid = (target = undefined) => {
    // FIRST PRESS FETCHES THE GRID. Nothing was loaded at model open -
    // see setBuildingGridLoader. The button state is deliberately NOT
    // toggled here: _ensureBuildingGrid calls back into onShowGrid when
    // the data lands, and that call does the toggle. Toggling twice
    // would light the button and then darken it under a visible grid.
    if (!this.scene.grid && this._ensureBuildingGrid()) {
      this._gridWanted = true
      return
    }
    if (!this.scene.grid) {
      const grid = this._buildBuildingGrid() || this._buildReferenceGrid()
      this.scene.add(grid)
      this.scene.grid = grid
      const labelLines = grid.userData.labelLines || []
      if (labelLines.length && !this._gridLabelOverlay && this.container) {
        this._gridLabelOverlay = new GridLabelOverlay(this.container)
      }
      if (this._gridLabelOverlay) this._gridLabelOverlay.setLines(labelLines)
      this._setGridVisible(true)

      // SAY WHICH GRID THIS IS. The two are not interchangeable and the
      // difference is not visible on screen: one is the building's own
      // named lines, the other an arbitrary lattice.
      const info = grid.userData.gridInfo || {}
      this.scene.gridInfo = info
      // An ambiguous grid is NOT a missing one, and saying so wrongly
      // sends somebody looking for an export that is already there.
      const why = info.kind === 'building'
        ? (info.confidence === 'known'
          ? 'Building grid - ' + info.lines + ' lines from ' + info.document
          : 'Building grid (best match) - ' + info.lines + ' lines from ' + info.document
            + '; no document is confirmed as the source, so this is the likeliest')
        : info.reason === 'ambiguous'
          ? 'Reference grid - ' + info.candidates + ' documents carry a grid and which one is the building’s is not known yet'
          : 'Reference grid - this project has no exported building grid'
      if (target) target.title = why
      const notify = this.ReactApi && typeof this.ReactApi.notify === 'function'
        ? (m) => this.ReactApi.notify(m)
        : null
      if (notify && info.kind === 'reference') {
        notify(info.reason === 'ambiguous'
          ? info.candidates + ' documents in this project carry a grid - showing a reference grid until which one is the building’s can be resolved.'
          : 'This project has no exported building grid - showing a reference grid instead.')
      }
      if (notify && info.labelsSuppressed) {
        notify('Showing ' + info.lines + ' grid lines without labels - too many to label legibly.')
      }
    } else {
      this._setGridVisible(!this.scene.grid.visible)
    }

    if (target) {
      target.classList.toggle('buttonEnabled')
    }
  }

  toggleMeasurement = () => {
    if (this.scene.clipping) this.toggleClipping()

    const target = document.getElementById('measurementToggleBtn')
    if (this.scene.measure == undefined) {
      this.scene.measure = true
    } else {
      this.scene.measure = !this.scene.measure
    }

    this.dimensionClass.active = this.scene.measure
    this.dimensionClass.previewActive = this.scene.measure
    
    if (this.scene.measure) {
      this.scene.add(this.intersectionIndicator)
    }
    
    if (!this.scene.measure) {
      this.intersectionIndicator.position.copy(new Vector3(0, 0, 0))
      this.scene.remove(this.intersectionIndicator)
    }

    document.body.style.cursor = this.scene.measure && this.renderer.mouseIsOver ? measureCursor : 'auto'
    
    if (target) {
      target.classList.toggle('buttonEnabled')
    }
  }

  toggleClipping = () => {
    const target = document.getElementById('clippingToggleBtn')
    this.clipper.updateTooltip()

    if (this.scene.measure) this.toggleMeasurement()

    if (this.scene.clipping == undefined) {
      this.scene.clipping = true
    } else {
      this.scene.clipping = !this.scene.clipping
    }

    if (this.scene.clipping) {
      this.scene.add(this.clipper.tempClipperPlane)
    }
    
    if (!this.scene.clipping) {
      this.clipper.tempClipperPlane.position.copy(new Vector3(0, 0, 0))
      this.scene.remove(this.clipper.tempClipperPlane)
      this.clipper.setTooltip('')
    }

    if (target) {
      target.classList.toggle('buttonEnabled')
    }
  }

// toggleIsolate — show only the currently-selected uids, hide the rest.
//
// Streaming-aware: when this.streaming?.state exists, route through the
// state store. The store remembers which uids are isolated; the
// applicator finds slots and writes correct visibility to instanceMatrix
// — and re-applies on every tile rematerialize. Result: isolate state
// survives:
//   - tile unload + reload (camera leaves frustum and returns)
//   - LOD swap
//   - tile boundary promotion
//
// Legacy non-streaming path: unchanged from original implementation.
// Walks meshesObject building per-mesh visibility plans, writes directly
// to instanceMatrix using the centeredMatrices snapshot. This works for
// legacy projects because nothing unloads — the entire model is in the
// scene from initial load.
// HIDE THE CURRENT SELECTION - the owner's "hide few elements also bulk
// hide like the model view", 2026-09-22.
//
// It reads `totalSelection`, the SAME list toggleIsolate reads below and
// for the same reason: every selection path maintains it - a plain
// click, a ctrl-click, and selectUids for a tree row or a Ctrl+drag box.
// So one read covers all three cases the owner asked for, and hide and
// isolate can never disagree about what "the selection" means.
//
// byHand is the point of it. The tracking page hides and unhides through
// this same store from its status legend; without the mark, a status
// toggled back on would reveal what a person deliberately hid. The store
// refuses that (ViewerStateStore.bulkShow) - it is not this caller's job
// to remember, nor the legend's.
//
// Returns false rather than declining quietly: a control that does
// nothing looks broken, so the page can say why instead.
hideSelected = () => {
  const store = this.streaming?.state
  // TWO SELECTIONS, NOT ONE - and my own survey asserted otherwise, which
  // is what made this wrong. The viewer holds totalSelection; the
  // TRACKING page holds its own, painted through highlightUids, and its
  // box, tree and quantities picks never reach selectUids. So on the page
  // the owner actually asked about, a box of 687 elements left
  // totalSelection EMPTY and Hide acted on nothing (root-8e, headless on
  // staging main.142043be, 2026-09-22). A page that keeps no second
  // selection offers no pageSelection, and behaves exactly as before.
  // NESTED PARTS MOVE WITH THEIR ELEMENT. Hiding a trapeze must take its
  // rods, nuts and washers with it - they are its geometry, drawn as
  // their own instance rows since 2026-09-24. Hiding one nut of ninety
  // and leaving the rest is not what anyone means by hiding a hanger.
  // expandToFamily also works the other way: acting on a PART acts on the
  // whole family, because the part is not a thing on its own.
  const uids = expandToFamily(selectionForHide(this.totalSelection, pageSelectionOf(this.ReactApi)))
  const notify = this.ReactApi && typeof this.ReactApi.notify === 'function'
    ? (m) => this.ReactApi.notify(m)
    : null
  if (!uids.length) {
    if (notify) notify('Select something first, then hide it.')
    return false
  }
  if (!store) {
    // Legacy (non-streaming) projects have no state store. Say so rather
    // than appearing to work.
    if (notify) notify('Hiding is not available on this model.')
    return false
  }
  store.bulkHide(uids, { byHand: true })

  // Drop the selection with it. A selection you cannot see is one that
  // every following command still acts on: frame selection would fly the
  // camera to nothing, and isolate would isolate the invisible.
  this.clearAllSelections('prevMeshes', 'prevMaterials')

  // And let the PAGE drop its own, for the same reason. Clearing only the
  // viewer's half would leave tracking reporting "Selected in the model:
  // 687" for elements nobody can see.
  if (this.ReactApi && typeof this.ReactApi.onHidden === 'function') {
    try { this.ReactApi.onHidden(uids) } catch (e) { /* the page's handler */ }
  }

  this.requestRender(0)
  return true
}

// The one way back, and deliberately the only one: showAll is where the
// store clears the by-hand marks, so nothing a page derives can undo a
// person's hide by accident.
unhideAll = () => {
  const store = this.streaming?.state
  if (!store) return false
  store.showAll()
  this.requestRender(0)
  return true
}

toggleIsolate = (target = undefined) => {
  // `totalSelection` is the authoritative current-selection list,
  // maintained by both onClickChildInList and onClickChildInListSocket
  // — non-ctrl click resets it, ctrl-click adds/removes, deselect/reset
  // shrink it. Use it directly.
  //
  // Why we no longer union with cachedMeshSelections: that map keeps
  // a per-tree selection HISTORY for things like "which uids in tree 1
  // were last selected so the React side can highlight them." It is
  // NOT cleared cross-tree when the user does a non-ctrl plain click
  // in tree 0 (TimelineViewer's clearOtherTrees only resets the React
  // clickedChild state, not the viewer-side per-tree cache). Unioning
  // them into the isolate set means a stale selection in another tree
  // gets isolated alongside the one the user actually selected — the
  // "isolate is isolating the wrong selection" symptom.
  //
  // totalSelection isn't subject to the same drift; both code paths
  // update it on every select / ctrl-click / deselect / reset.
  //
  // ...EXCEPT that it is not the only selection. The tracking page keeps
  // its own, painted with highlightUids, so Isolate had nothing to
  // isolate there for exactly the reason Hide did, and setIsolation(null)
  // made that a SILENT no-op: the button lit up, the tooltip said "0
  // objects isolated" and the model did not change. Same union as Hide
  // and framing, resolved once and used by every branch below.
  const picked = selectionForHide(this.totalSelection, pageSelectionOf(this.ReactApi));
  if (!picked.length && !this.isolateMode) {
    // Say so instead of toggling into an isolation of nothing.
    if (this.ReactApi && typeof this.ReactApi.notify === 'function') {
      this.ReactApi.notify('Select something first, then isolate it.');
    }
    return;
  }
  this.isolateMode = !this.isolateMode;

  // Streaming path: route through state store. Applicator handles the
  // matrix writes via subscription; we just tell the store what to
  // isolate / clear. The store full-sweep emit triggers the applicator
  // to walk every active mesh and recompute visibility per instance.
  const streamingState = this.streaming?.state;

  if (streamingState) {
    if (this.isolateMode) {
      // THE FAMILY, not just the element. Isolating a trapeze and
      // leaving its rods and nuts behind would show a bare strut - the
      // parts are its geometry, drawn as their own rows since
      // 2026-09-24. Selection holds ELEMENTS (a pick resolves a part to
      // its parent), so this expands outward; given a part it expands
      // both ways and lands on the same family.
      streamingState.isolate(expandToFamily(picked));
    } else {
      streamingState.clearIsolation();
    }

    const tooltip = document.getElementById('clippingTooltip');
    if (tooltip) {
      tooltip.innerText = this.isolateMode
        ? `${picked.length} object${picked.length !== 1 ? 's' : ''} isolated`
        : '';
    }

    if (target) target.classList.toggle('buttonEnabled');
    this.scene.updateMatrixWorld(true);
    return;
  }

  // ----- Legacy non-streaming path ----------------------------------
  //
  // Below is the original implementation, used when streaming.state
  // isn't set up (legacy projects without manifests). It writes
  // directly to instanceMatrix and tracks pre-isolation state on
  // this.preIsolationState so toggling off restores correctly.

  if (this.isolateMode) {
    // Legacy path: use totalSelection directly. Previously this read a
    // cross-tree `allSelections` union built at the top of the function;
    // that union is gone (see streaming branch comment above), so build
    // a Set view of totalSelection here instead. Same semantics for
    // legacy projects with only one tree's worth of selections.
    const selectedIds = new Set(picked);
    this.preIsolationState = new Map();

    const visibilityChanges = new Map();

    for (const [key, value] of this.meshesObject.entries()) {
      let shouldShow = false;
      let perFragVisible = null;

      if (selectedIds.has(key) || (value.uniqueId && selectedIds.has(value.uniqueId))) {
        shouldShow = true;
      }

      if (Array.isArray(value.fragments) && value.fragments.length > 0) {
        const localPlan = new Map();

        value.fragments.forEach(({ mesh }) => {
          if (!this.preIsolationState.has(mesh.uuid)) {
            const currentlyVisible = new Set();
            const m = new Matrix4();
            const s = new Vector3();
            for (let i = 0; i < mesh.count; i++) {
              mesh.getMatrixAt(i, m);
              m.decompose(new Vector3(), new Quaternion(), s);
              if (s.x > 0.0002) currentlyVisible.add(i);
            }
            this.preIsolationState.set(mesh.uuid, currentlyVisible);
          }

          if (mesh.userData) {
            for (const [instanceId, instanceData] of Object.entries(mesh.userData)) {
              if (instanceData?.uniqueId && selectedIds.has(instanceData.uniqueId)) {
                shouldShow = true;
                if (!localPlan.has(mesh.uuid)) localPlan.set(mesh.uuid, []);
                localPlan.get(mesh.uuid).push(parseInt(instanceId, 10));
              }
            }
          }
        });

        if (localPlan.size > 0) perFragVisible = localPlan;

      } else if (!shouldShow && value.mesh && value.mesh.isInstancedMesh) {
        if (!this.preIsolationState.has(value.mesh.uuid)) {
          const currentlyVisible = new Set();
          const m = new Matrix4();
          const s = new Vector3();
          for (let i = 0; i < value.mesh.count; i++) {
            value.mesh.getMatrixAt(i, m);
            m.decompose(new Vector3(), new Quaternion(), s);
            if (s.x > 0.0002) currentlyVisible.add(i);
          }
          this.preIsolationState.set(value.mesh.uuid, currentlyVisible);
        }

        if (value.mesh.userData) {
          for (const [instanceId, instanceData] of Object.entries(value.mesh.userData)) {
            if (instanceData?.uniqueId && selectedIds.has(instanceData.uniqueId)) {
              shouldShow = true;
              perFragVisible = new Map([
                [value.mesh.uuid, [parseInt(instanceId, 10)]],
              ]);
            }
          }
        }
      }

      if (!shouldShow && value.mesh && value.mesh.parent) {
        let parent = value.mesh.parent;
        let depth = 0;
        const maxDepth = 10;
        while (parent && depth < maxDepth) {
          if (selectedIds.has(parent.name) || selectedIds.has(parent.uuid)) {
            shouldShow = true;
            break;
          }
          parent = parent.parent;
          depth++;
        }
      }

      visibilityChanges.set(key, { shouldShow, perFragVisible });
    }

    for (const [key, plan] of visibilityChanges.entries()) {
      const value = this.meshesObject.get(key);

      if (plan.perFragVisible && Array.isArray(value.fragments) && value.fragments.length > 0) {
        value.fragments.forEach(({ mesh }) => {
          const indices = plan.perFragVisible.get(mesh.uuid) || [];
          if (this.isCentered && mesh.userData.centeredMatrices) {
            this.applyInstancedMeshVisibility(mesh, indices);
          }
          mesh.visible = true;
        });
        value._perFragVisible = plan.perFragVisible;

      } else if (plan.perFragVisible && value.mesh && value.mesh.isInstancedMesh) {
        const indices = plan.perFragVisible.get(value.mesh.uuid) || [];
        if (this.isCentered && value.mesh.userData.centeredMatrices) {
          this.applyInstancedMeshVisibility(value.mesh, indices);
        }
        value.mesh.visible = true;

      } else {
        UpdateGroupVisibility(value, plan.shouldShow);
      }
    }

    const tooltip = document.getElementById('clippingTooltip');
    if (tooltip) {
      tooltip.innerText = `${picked.length} object${picked.length !== 1 ? 's' : ''} isolated`;
    }
  } else {
    this.meshes.forEach(mesh => {
      mesh.visible = true;
      if (mesh.isInstancedMesh && this.preIsolationState) {
        const visibleInstances = this.preIsolationState.get(mesh.uuid);
        if (visibleInstances && visibleInstances.size > 0) {
          if (this.isCentered && mesh.userData.centeredMatrices) {
            this.applyInstancedMeshVisibility(mesh, Array.from(visibleInstances));
          }
        } else if (this.isCentered && mesh.userData.centeredMatrices) {
          this.applyInstancedMeshVisibility(mesh, []);
        }
      }
    });

    for (const [, value] of this.meshesObject.entries()) {
      UpdateGroupVisibility(value, true);
      if (value.mesh && !isSlotBacked(value.mesh)) value.mesh.visible = true;
      if (value._perFragVisible) delete value._perFragVisible;
    }

    this.preIsolationState = null;

    const tooltip = document.getElementById('clippingTooltip');
    if (tooltip) tooltip.innerText = '';
  }

  if (target) target.classList.toggle('buttonEnabled');
  this.scene.updateMatrixWorld(true);
};

  // True when the streaming pipeline is actively pulling content. Used
  // by the animate() throttle below — see comment there for why.
  //
  // Reads through viewer.streaming.stats(): that's the only public
  // surface that exposes the GLBFetcher state externally. (`streaming`
  // does NOT directly expose `.controller` or `.fetcher` — that was the
  // earlier bug where the throttle never engaged because the path was
  // always undefined.)
  // Streaming busy = any tile load or fragment fetch in flight. Read
  // from the controller's and fetcher's counters; the old version called
  // streaming.stats(), which walks every tile, pool and slot (three
  // nested aggregations) - ~20 % of all main-thread samples on SpaceX,
  // once per animation frame, only to decide whether to render.
  // Should this (not busy) frame be drawn? Sets up the input listeners
  // the first time: any pointer, wheel or key on the page asks for a
  // second of frames, since that is how most scene changes start.
  _drawThisFrame(now) {
    if (this.renderOnDemand === false || !renderOnDemandEnabled()) return true;
    if (!this._rod) {
      this._rod = new RenderOnDemand();
      const wake = () => { if (this._rod) this._rod.request(performance.now(), this._rod.inputMs); };
      const events = ['pointerdown', 'pointermove', 'pointerup', 'wheel', 'keydown', 'keyup', 'touchstart', 'touchmove'];
      events.forEach((e) => window.addEventListener(e, wake, { passive: true, capture: true }));
      this._rodUnlisten = () => events.forEach((e) => window.removeEventListener(e, wake, { capture: true }));
    }
    const canvas = this.renderer && this.renderer.domElement;
    if (this.camera && this.camera.updateMatrixWorld) this.camera.updateMatrixWorld();
    return this._rod.shouldDraw({
      now,
      camera: this.camera,
      width: canvas ? canvas.width : 0,
      height: canvas ? canvas.height : 0,
      sceneVersion: sceneVersion(),
      busy: false,
    });
  }

  // Ask for frames from code that changed the picture without going
  // through the pools (renderSignal) - e.g. a material or a helper.
  requestRender(ms = 0) {
    if (this._rod) this._rod.request(performance.now(), ms);
  }

  // The two shader effects (surfaceShading.js). The page owns the choice
  // and remembers it per user; the viewer just carries it to the shared
  // uniforms and asks for a frame.
  //   viewer.setShading({ edges: true, contact: false })
  setShading(next) {
    const n = next || {};
    const changed = setSurfaceShading(n);
    // The depth composite (composite/surfaceComposite.js) - off by
    // default, and built only when it is first asked for.
    let compositeChanged = false;
    for (const [k, v] of Object.entries(n)) {
      if (k === 'edges' || k === 'contact' || k === 'edgeSharpness') continue;
      const key = k === 'composite' ? 'enabled' : k;
      if (!(key in compositeState) || compositeState[key] === v) continue;
      compositeState[key] = v;
      compositeChanged = true;
    }
    if (compositeState.enabled && !this._composite) this._composite = new SurfaceComposite(this.renderer);
    // The composite lives in the composer chain, so the outline pass can
    // draw over it instead of replacing it (compositePass.js).
    if (this.postProcessing && this.postProcessing.useComposite) {
      this.postProcessing.useComposite(compositeState.enabled ? this._composite : null);
    }
    if (changed || compositeChanged) this.requestRender(0);
    return this.shadingState();
  }

  shadingState() {
    return { ...surfaceShadingState(), composite: { ...compositeState } };
  }

  _isStreamingBusy() {
    try {
      const st = this.streaming;
      const c = st && (st.controller || st.streamingController);
      if (!c) return false;
      if ((c._activeTileLoads || 0) > 0) return true;
      const f = c.fetcher;
      if (f) {
        if ((f._activeFetches || 0) > 0) return true;
        if (f._fetchQueue && f._fetchQueue.length > 0) return true;
        if (f.inFlight && f.inFlight.size > 0) return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }
  // ONE ANIMATION CHAIN PER VIEWER. animate() re-schedules itself through
  // requestAnimationFrame, and the constructor starts it; the loaders
  // (streamingLoader, loader/index.js) used to call animate() again when a
  // model finished loading, which started a SECOND self-scheduling chain.
  // From then on every displayed frame was drawn twice (Tom, real GPU,
  // 2026-10-13: 654 drawFrame calls for 327 rAFs, 25-33 ms CPU each), and
  // renderer.info - reset once per animate - summed both, so perfSnapshot
  // reported twice the triangles and calls the screen needed.
  // A call from outside while a chain is alive is now a no-op; the chain
  // passes its own token and a stale one stops.
  animate(nowMsec, chain) {
    if (chain === undefined) {
      if (this._animChain && !this._destroyed) { this._animDuplicateStarts = (this._animDuplicateStarts || 0) + 1; return; }
      chain = this._animChain = {};
    } else if (chain !== this._animChain) {
      return;
    }
    // renderer.info is read by perfSnapshot(); three resets it at every
    // render() call, and the composer's last pass is a full-screen
    // triangle, so a read at snapshot time saw "1 call, 1 triangle".
    // Accumulate over the whole frame instead: reset here, read after
    // the composer has drawn (see the nested RAF callback).
    if (this.renderer && this.renderer.info) {
      // Copy the finished frame's totals before the reset - whatever
      // passes ran (direct and/or composer; throttled frames skip the
      // composer), so perfSnapshot().gpu is never a stale composer-only
      // frame.
      const info = this.renderer.info;
      const fs = this.__frameStats || (this.__frameStats = { render: [], interval: [], last: 0, gpu: null });
      if (info.render && (info.render.calls || info.render.triangles)) {
        fs.gpu = { calls: info.render.calls, triangles: info.render.triangles, lines: info.render.lines, points: info.render.points };
      }
      info.autoReset = false;
      info.reset();
    }
    // Torn down: stop the self-scheduling RAF chain. Without this the
    // loop outlives the pane that owned it and keeps rendering into a
    // detached canvas whose renderer the caller has already disposed -
    // which is every surface switch on the issue page.
    if (this._destroyed) { this._animChain = null; return; }

    // === STREAMING-AWARE RENDER THROTTLE ===
    //
    // User report: while the CefSharp browser window is VISIBLE, cold
    // load is glacial; minimizing or otherwise hiding the browser makes
    // the model populate in a few seconds. Root cause: when visible,
    // Chromium runs requestAnimationFrame at 60 Hz, and three.js's
    // composer + scene render eat most of the 16 ms frame budget. Fetch
    // → parse → instanceMaterializer.materialize all compete for the
    // remaining few ms of main-thread time, so they progress crawl-slow.
    // When hidden, Chromium throttles RAF to ~1 Hz; render does almost
    // nothing, so 100 % of the main thread goes to streaming work, and
    // the model loads in seconds.
    //
    // Fix: detect "streaming busy" state (any fetch in flight) and
    // throttle our own render to ~1 FPS (1000 ms) during that window.
    // 1 FPS matches the actual hidden-browser RAF rate that produced
    // the fast-load behavior the user observed. The scene will look
    // visibly stuttery while streaming — that's the intentional
    // trade. Once _isStreamingBusy returns false, throttle releases
    // back to 60 FPS for normal interaction.
    const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    const busy = this._isStreamingBusy();
    // While tiles are materialising, render every ~66 ms (15 fps) so
    // streaming keeps most of the frame (measured: at 30 fps the tail of
    // SpaceX never finished - render 35-45 ms + an 18 ms slice per 55 ms
    // frame left streaming a third); when only fetches are pending, 33 ms.
    // ADAPTIVE: the interval follows what a frame actually costs on THIS
    // canvas. A fixed 66 ms let a 50 ms frame own most of the wall time
    // on a big canvas, which is why the model loaded faster with the
    // window shrunk or hidden (owner, 2026-09-14): everything that turns
    // bytes into geometry runs on the main thread between frames.
    const __c = this.streaming && this.streaming.controller;
    const __fs = this.__frameStats;
    this.busyRenderIntervalMs = Viewer.busyRenderInterval(__fs ? __fs.render : null, !!(__c && (__c._activeTileLoads || 0) > 0));
    let skipFrame = false;
    if (busy) {
      if (!this._lastBusyRenderMs) this._lastBusyRenderMs = 0;
      // Render at ~30 fps while streaming instead of 1 fps. The 1 s gate
      // predates the per-frame materialisation slice (12 ms) that now
      // protects the frame budget; with it, the gate only made the
      // whole 90-190 s load look frozen (measured: interval pinned at
      // 1,000 ms with the main thread 57 % idle).
      if (now - this._lastBusyRenderMs < this.busyRenderIntervalMs) skipFrame = true;
      else {
        this._lastBusyRenderMs = now;
      }
    } else {
      this._lastBusyRenderMs = 0;
    }

    requestAnimationFrame(() => {
      // (no timestamp passed, exactly as before: the chain has always run
      // with nowMsec undefined after the first frame)
      this.animate(undefined, chain);
      // Inside the nested RAF callback: also honor the throttle so the
      // heavy composer.render doesn't run while we're "skipping" a frame.
      // The recursive this.animate() above re-schedules the next frame,
      // so the chain stays alive even when we return early here.
      // The ORIENTATION CUBE is drawn before the throttle returns.
      // It used to sit after it, so while streaming skipped frames the
      // cube froze with them - an indicator that lies about your
      // orientation exactly when the model is moving under load is worse
      // than no indicator. It is a 150x150 draw of its own tiny scene;
      // it is not what a loading frame is spending its time on.
      // ON DEMAND, not every frame. The cube is a second WebGL context
      // and it was redrawn on EVERY animation frame, whether or not
      // anything about it had changed - a whole context switch and a
      // draw to tell you the same thing it told you last frame.
      //
      // It depends on ORIENTATION ALONE, so comparing the camera's
      // quaternion is enough, and comparing four numbers in place costs
      // nothing. The reason it sits ABOVE the throttle still holds and
      // is preserved: when the camera IS turning the cube redraws even
      // on skipped frames, because an orientation indicator that freezes
      // while the model moves is worse than none.
      if (this.viewerHelper && this.viewHelperRenderer && this._cubeMoved()) {
        this.viewerHelper.render(this.viewHelperRenderer);
      }
      if (skipFrame) return;

      const delta = this.clock.getDelta();

      // CSS2DRenderer.render walks the whole scene graph (7k pool
      // meshes on SpaceX) every frame; only worth it while there are
      // dimension labels to place or a measurement preview is live.
      // The building grid's labels no longer need it: they are drawn on
      // their own canvas after the frame (gridLabelOverlay), which is
      // what took the grid's per-frame scene walk away.
      if (this.dimensionClass && ((this.dimensionClass.dimensions && this.dimensionClass.dimensions.length) || this.scene.measure)) {
        this.dimensionClass.renderer2D.render(this.scene, this.camera);
      }

    // (the cube is drawn above, before the throttle returns)

    // Update camera controls
    if (this.cameraControls.enabled) {
      const updated = this.cameraControls.update(delta);
        if (!updated && this.OrbitControls.enabled) {
          this.OrbitControls.update();
        }
      } else if (this.OrbitControls.enabled) {
        this.OrbitControls.update();
      }

      // Auto spin
      if (this.autoSpin && this.cameraControls) {
        const speed = (this.ReactApi && this.ReactApi.flags && 
                      typeof this.ReactApi.flags.spinSpeed === "number" &&
                      this.ReactApi.flags.spinSpeed) || 0.2;
        this.cameraControls.rotate(-delta * speed, 0, false);
      }

      // Update all instanced meshes
// Lazy boundingSphere init for raycast/frustum support. Skip the
// per-frame updateMatrixWorld walk — meshes don't move, and three.js
// renders already call updateMatrixWorld internally when they need
// to. The previous code did 576 forced walks per frame plus a forced
// scene-wide walk; benign on small scenes, capped FPS hard at scale.
//
// If a mesh's transform actually changes (rare — mostly during
// streaming load/unload, which the streaming controller handles
// itself), three.js will refresh world matrices on the next render.
for (let i = 0; i < this.meshes.length; i++) {
  const mesh = this.meshes[i];
  if (mesh.isInstancedMesh && mesh.geometry.boundingSphere === null) {
    mesh.geometry.computeBoundingSphere();
  }
}

      // Nothing moved and nothing changed: the canvas already shows this
      // frame, so skip drawing it (see renderOnDemand.js). Controls,
      // tweens and spin above/below still run every frame, which is how
      // a move is noticed on the next one.
      if (!busy && !this._drawThisFrame(performance.now())) {
        TWEEN.update(nowMsec);
        return;
      }

      // The composer (RenderPass + OutlinePass) is what reaches the
      // canvas. Timed for perfSnapshot(); the frame's accumulated
      // renderer.info is copied out here, after the last draw.
      const __c0 = (typeof performance !== 'undefined') ? performance.now() : 0;
      if (this._composite) this.__camKeyNow = SurfaceComposite.cameraKey(this.camera);
      // While streaming, and with nothing outlined, the scene goes
      // straight to the canvas. The composer's render pass draws into an
      // offscreen target and the outline pass then copies it full-screen
      // even with nothing to outline - two pixel-bound passes a loading
      // frame does not need. The composer comes back the moment a hover
      // outline exists or the loading ends, so the picture is unchanged.
      // With the composite on this shortcut is off: the effects would
      // otherwise be absent for the whole load and appear when it settles,
      // and the owner has already reported that shape of thing twice.
      // The composite is a PASS now (compositePass.js), so there is no
      // choice to make and no hover special-case: the composer draws the
      // frame, the composite fills the buffer and the outline pass draws
      // over it. The only remaining shortcut is the streaming one below.
      const moving = this.__lastCamKey !== undefined && this.__lastCamKey !== this.__camKeyNow;
      this.__lastCamKey = this.__camKeyNow;
      this.drawFrame({ delta, moving, busy });
      // Grid labels follow the frame just drawn; a still view is one
      // comparison and no drawing (gridLabelOverlay keys on the camera).
      if (this._gridLabelOverlay) {
        const g = this.scene.grid;
        this._gridLabelOverlay.draw(this.camera, !!(g && g.visible));
      }
      if (__c0) {
        const fs = this.__frameStats || (this.__frameStats = { render: [], interval: [], last: 0, gpu: null });
        const t1 = performance.now();
        fs.render.push(t1 - __c0); if (fs.render.length > 120) fs.render.shift();
        if (fs.last) { fs.interval.push(t1 - fs.last); if (fs.interval.length > 120) fs.interval.shift(); }
        fs.last = t1;
      }
      TWEEN.update(nowMsec);
    });

    lastTimeMsec = lastTimeMsec || nowMsec - 1000 / 60;
    var deltaMsec = Math.min(200, nowMsec - lastTimeMsec);
    lastTimeMsec = nowMsec;

    // Honor the throttle here too — the direct-body renderer.render
    // below is just as heavy as the composer.render inside the RAF
    // callback above. Skipping it gives parse + materialize the full
    // 100 ms slice on busy frames.
    if (skipFrame) return;

    updateFcts.forEach(function (updateFn) {
      updateFn(deltaMsec / 1000, nowMsec / 1000);
    });

    if (this.divSize.h != this.container.offsetHeight ||
        this.divSize.w != this.container.offsetWidth) {
      this.divSize = {
        h: this.container.offsetHeight,
        w: this.container.offsetWidth,
      };
      this.onWindowResize();
    }

    this.renderer.setViewport(
      0,
      0,
      this.container.offsetWidth,
      this.container.offsetHeight
    );

    this.renderer.localClippingEnabled = true;
    this.renderer.clippingPlanes = this.clipper.clippingPlanes;
    // The scene used to be drawn TWICE per frame: this direct render
    // here, then the composer's RenderPass (+ OutlinePass) in the nested
    // RAF callback, whose output is what the canvas shows - so this pass
    // was overwritten every frame and only cost GPU time (on SpaceX BA03
    // that is a full draw of ~100k instanced meshes thrown away). Skipped
    // whenever the composer exists; set
    // viewer.skipDirectRenderWithComposer = false from the console to
    // A/B the old behaviour.
    const composing = !!(this.postProcessing && this.postProcessing.composer);
    if (!composing || this.skipDirectRenderWithComposer === false) {
      this.drawFrame();
    }
  }



  // _revertPrevMesh — undo what addSelection did. Centralizes handling
  // of multi-fragment, single-instance, and non-instanced cases so each
  // call site doesn't have to.
  //
  // Streaming-aware: when streaming.state exists, route unhighlight
  // through the store. The store's clearHighlight removes the entry,
  // applicator re-applies (writing baseColors to the slot). This stays
  // correct across rematerialization because store state is durable.
  _revertPrevMesh(prevMesh, materials) {
    if (!prevMesh) return;

    const streamingState = this.streaming?.state;
    const uid = prevMesh.parentNodeId;

    if (Array.isArray(prevMesh.instanceIndices) && Array.isArray(prevMesh.meshes)) {
      // Multi-fragment instanced path.
      if (streamingState && uid) {
        streamingState.clearHighlight(uid);
      } else {
        prevMesh.meshes.forEach((m, i) => {
          this.unhighlightInstance(m, prevMesh.instanceIndices[i]);
        });
      }
      return;
    }

    if (prevMesh.instanceIndex !== undefined && isSlotBacked(prevMesh.meshes?.[0])) {
      // Single-instance path.
      if (streamingState && uid) {
        streamingState.clearHighlight(uid);
      } else {
        this.unhighlightInstance(prevMesh.meshes[0], prevMesh.instanceIndex);
      }
      return;
    }

    if (prevMesh.meshes && prevMesh.meshes.length > 0) {
      // Non-instanced path — unchanged. Restores material from
      // captured pre-selection material list.
      SetChildrenMaterialsBack(prevMesh.meshes, materials);
    }
  }

}